import type { DiscoveryReadinessContract } from "../types/discoveryReadiness";
import type { ArchetypeSmokeResult } from "../types/archetype";

type FoundryV2ApiErrorPayload = {
  ok?: boolean;
  error?: string;
  code?: string;
  readiness?: DiscoveryReadinessContract;
  validationErrors?: FoundryContractV1ValidationError[];
};

export interface FoundryV2ContextGateResponse {
  context: {
    bundle: {
      projectId: string;
      project?: {
        id?: string;
        name?: string | null;
        code?: string | null;
      };
      customer?: {
        id?: string;
        name?: string | null;
      };
      keyFacts?: {
        facts?: string[];
        assumptions?: string[];
        risks?: string[];
        unknowns?: string[];
      };
      meetingTranscripts?: unknown[];
      documentAnalyses?: unknown[];
      discovery?: {
        discovery?: {
          notes?: unknown[];
        };
      };
    };
    prompt: string;
  };
  readiness: DiscoveryReadinessContract;
}

export interface FoundryContractV1 {
  schema: "longwalls.foundry.contract@1";
  version: string;
  projectId: string;
  createdAt: string;
  transcript: {
    source: string;
    language: string | null;
    text: string;
  };
  hypotheses: string[];
  openQuestions: string[];
  mvpScope: string[];
  nfr: string[];
  risks: string[];
  kpis: string[];
}

export interface FoundryContractV1Assessment {
  generatedAt: string;
  confidenceScore: number;
  synthesizedHypotheses: string[];
  generatedQuestions: string[];
  blockingReasons: string[];
  isReadyForSpec: boolean;
  readinessDecision: "pass" | "fail" | "needs_approval";
  readinessStatus: string;
}

export interface FoundryTechnicalSpecDraftV1 {
  schema: "longwalls.technical-spec.draft@1";
  version: string;
  projectId: string;
  generatedAt: string;
  summary: string;
  architectureTarget: {
    appType: string;
    runtime: string;
    frontend: string;
    backend: string;
    database: string;
    deployment: string;
    auth: string;
    observability: string;
    rationale: string;
    confidence: number;
  };
  dataModel: Array<{
    entity: string;
    purpose: string;
    sourceScope: string;
  }>;
  endpoints: Array<{
    method: "GET" | "POST" | "PUT" | "PATCH";
    path: string;
    description: string;
  }>;
  initialBacklog: Array<{
    id: string;
    title: string;
    type: "feature" | "spike" | "hardening";
    priority: "must" | "should";
    acceptanceCriteria: string[];
  }>;
  traceability: {
    transcriptSource: string;
    topAssumptions: string[];
    topOpenQuestions: string[];
    topRisks: string[];
  };
}

export interface FoundryContractV1DraftInput {
  transcriptText?: string;
  transcriptSource?: string;
  transcriptLanguage?: string;
  hypotheses?: string[] | string;
  openQuestions?: string[] | string;
  mvpScope?: string[] | string;
  nfr?: string[] | string;
  risks?: string[] | string;
  kpis?: string[] | string;
  appType?: string;
}

export interface FoundryContractV1ValidationError {
  field: string;
  code: string;
  message: string;
}

export interface FoundryContractV1DraftRecord {
  id: string;
  projectId: string;
  createdAt: string;
  contract: FoundryContractV1;
  assessment: FoundryContractV1Assessment;
  specDraft: FoundryTechnicalSpecDraftV1;
  artifactUrls: {
    contract: string;
    assessment: string;
    specDraft: string;
  };
}

export interface FoundryLegacyExternalIntakeRecord {
  schema: "longwalls.foundry-external-intake@1";
  version: "1";
  generatedAt: string;
  projectId: string;
  repoId: string;
  status: "ready_for_foundry" | "blocked";
  repositoryName: string;
  sourceRepoPath: string;
  confidenceScore: number;
  repoSummary: {
    title: string;
    sponsorNarrative: string;
    domainHypothesis: string;
    frameworkSignals: string[];
    integrationSignals: string[];
    runtimeSignals: string[];
    gapCount: number;
  };
  foundryLift: {
    sectionIds: string[];
    moduleTitles: string[];
    nextMove: string;
  };
  environment: {
    requiredEnvKeys: string[];
    recommendedEnvKeys: string[];
    observedEnvKeys: string[];
    integrationTargets: string[];
    integrationNotes: string;
    runtimeProfile: "customer_server";
    githubMode: "existing-repo";
    repositoryName: string;
    complianceProfile: "restricted";
  };
  openQuestions: string[];
  blockers: string[];
  repoSignals: {
    acceptanceScenarios: string[];
    goLiveChecks: string[];
    sourceDocuments: string[];
  };
  contractInput: FoundryContractV1DraftInput;
  blueprintInput: FoundryV2HandoffInput;
  artifactUrls: {
    intake: string;
  };
}

export interface FoundryLegacyExternalIntakeResponse {
  intake: FoundryLegacyExternalIntakeRecord;
  contractDraft: FoundryContractV1DraftRecord;
  compositionDraft: FoundryV2CompositionDraftRecord;
}

export interface FoundryV2CompositionDraftRecord {
  id: string;
  projectId: string;
  createdAt: string;
  appName: string;
  projectCode: string | null;
  inputSnapshot: FoundryV2HandoffInput;
  foundryContract: {
    id: string;
    app: {
      name: string;
      type: string;
      businessGoal: string;
      archetypeId: string;
      recommendedBrickIds: string[];
    };
    brickSelection?: {
      status: "locked";
      rationale: string[];
      requiredBrickIds: string[];
      recommendedBrickIds: string[];
      optionalBrickIds: string[];
      rejectedBrickIds: string[];
      entries: Array<{
        brickId: string;
        title: string;
        summary: string;
        family: string;
        selection: "required" | "recommended" | "optional" | "rejected";
        source:
          | "planning-bundle"
          | "archetype-library"
          | "catalog-compatibility"
          | "governance-runtime"
          | "legacy-hydration";
        rationale: string[];
        sensitive: boolean;
      }>;
    };
    archetypeSelection: {
      status: "locked";
      archetypeId: string;
      title: string;
      summary: string;
      layoutFamily: string;
      workflowFamily: string;
      visualThesis: string;
      experienceLabel: string;
      workspaceMode: string;
      density: "compact" | "balanced" | "immersive";
      motionProfile: "restrained" | "expressive";
      heroNarrative: string;
      recommendedBrickIds: string[];
      securityDefaults: string[];
      rationale: string[];
    };
    domainKitSelection: {
      status: "locked";
      domainFamily: string;
      operatingModel: string;
      title: string;
      summary: string;
      selectedSliceIds: string[];
      governanceBrickIds: string[];
      mandatoryControlIds: string[];
      proofScenarioIds: string[];
      rationale: string[];
    };
    dataReadiness: {
      posture: "ready" | "watch" | "blocked";
      sources: string[];
      blockers: string[];
    };
    integrationPack: {
      targets: string[];
      envKeys: string[];
      complianceProfile: string;
    };
    uxThesis: {
      density: "compact" | "balanced" | "immersive";
      motion: "restrained" | "expressive";
      emphasis: string;
    };
    securityThesis: {
      roles: string[];
      approvalsRequired: string[];
      auditLevel: "standard" | "signed";
      rollbackRequired: boolean;
    };
    planningSummary: {
      status: "ready" | "watch" | "blocked";
      domainFamily: string;
      operatingModel: string;
      confidence: number;
      rationale: string[];
      moduleIds: string[];
      serviceIds: string[];
      customSliceIds: string[];
      primaryFlowIds: string[];
    };
    customDelta: {
      summary: string;
      domainRules: string[];
      customUiRequirements: string[];
      customIntegrations: string[];
      blockedUntil: string[];
    };
    customDeltaContract: {
      summary: string;
      domainRules: string[];
      customUiRequirements: string[];
      customIntegrations: string[];
      blockedUntil: string[];
    };
    securityPackSelection: {
      status: "locked";
      packId: string;
      title: string;
      controlIds: string[];
      approvalIds: string[];
      rbacTemplateId: string;
      rbacScopeIds: string[];
      tenantIsolationMode: "tenant-column" | "workspace-isolated" | "single-tenant" | "customer-boundary" | "schema-per-tenant";
      secretHandlingMode: "standard" | "managed" | "customer-managed" | "strict";
      ssoProfile: "not-required" | "sso-ready" | "sso-required";
      rollbackMode: "artifact-pack" | "controlled-cutover" | "tenant-wave" | "break-glass-recovery";
      sensitiveBrickIds: string[];
      evidenceBundleId: string;
      evidenceBundleArtifacts: string[];
      auditArtifacts: string[];
      rollbackArtifacts: string[];
      rationale: string[];
    };
    qaPackSelection: {
      status: "locked";
      packId: string;
      title: string;
      primaryFlowIds: string[];
      seededDataNeeds: string[];
      evidenceArtifacts: string[];
      rationale: string[];
    };
    buildBlockers: Array<{
      id: string;
      title: string;
      severity: "blocking" | "watch";
      source: "data-readiness" | "decision-trace" | "custom-delta" | "integration-pack" | "security-pack" | "qa-pack";
      resolution: string;
    }>;
    domainProfile?: {
      domainFamily: string;
      operatingModel: string;
      archetypeId: string;
      title: string;
      summary: string;
      rationale: string[];
      requiredBrickIds: string[];
      governanceBrickIds: string[];
      recommendedCustomSliceIds: string[];
      mandatoryControls: string[];
      stackOptions: Array<{
        id: string;
        label: string;
        summary: string;
        stack: string[];
        suitableWhen: string[];
        caution: string[];
        why: string[];
        recommended: boolean;
      }>;
      keyQuestions: string[];
      proofScenarios: string[];
    };
    decisionTrace?: {
      status: "ready" | "watch" | "blocked";
      criticalLockedCount: number;
      criticalTotalCount: number;
      blockerSectionIds: string[];
      resolvedDecisions: Array<{
        sectionId: string;
        optionId: string;
        label: string;
        summary: string;
        stack: string[];
        rationale: string[];
        impacts: string[];
        promotedAt: string;
        promotedBy: "ai" | "human" | "hybrid";
      }>;
      sections: Array<{
        id: string;
        title: string;
        criticality: "low" | "medium" | "high" | "critical";
        status: "not_started" | "ai_proposed" | "needs_input" | "in_discussion" | "locked";
        selectedOptionId: string | null;
        recommendedOptionId: string | null;
      }>;
    };
    platformProfile?: {
      developmentMode: "local_only" | "local_first" | "cloud_dev";
      productionHostingMode: "cloud_managed" | "customer_cloud" | "on_prem" | "hybrid";
      preferredStack: string;
      dataPlatform: string;
      authStrategy: string;
      qaStrategy: string;
      releaseStrategy: string;
      securityTier: "standard" | "restricted" | "highly_regulated";
      summary: string[];
    };
    acceptance: {
      desktopFlow: string;
      mobileFlow?: string | null;
      evidenceChecklist: string[];
    };
  };
  foundryContractV3?: {
    id: string;
    sourceContractId: string;
    app: {
      name: string;
      type: string;
      businessGoal: string;
      archetypeId: string;
    };
    specCompleteness: {
      status: "ready" | "watch" | "blocked";
      score: number;
      sections: Array<{
        id: string;
        title: string;
        status: "ready" | "watch" | "blocked";
        summary: string;
        requiredBeforeBuildReady: boolean;
        gaps: string[];
        contradictions: string[];
      }>;
      gaps: string[];
      contradictions: string[];
      requiredBeforeBuildReady: string[];
    };
    externalIngestion: {
      status: "ready" | "watch" | "blocked";
      score: number;
      projectOrigin: "atlas-native" | "external-project" | "greenfield";
      laneId: string;
      laneTitle: string;
      githubMode: string;
      repositoryName: string | null;
      topology: string;
      complianceProfile: string;
      auth: string;
      observability: string;
      integrationTargets: string[];
      requiredSignalsSatisfied: string[];
      requiredSignalsMissing: string[];
      blockerIds: string[];
      rationale: string[];
    };
    buildReadyDecision: {
      status: "build-ready" | "needs-input" | "blocked";
      score: number;
      customDelta: {
        summary: string;
        load: "standard" | "elevated" | "high";
        openQuestions: string[];
        customUiRequirements: string[];
        customIntegrations: string[];
      };
      productionBlockers: Array<{
        id: string;
        title: string;
        severity: "blocking" | "watch";
        source: "data-readiness" | "decision-trace" | "custom-delta" | "integration-pack" | "security-pack" | "qa-pack";
        resolution: string;
      }>;
      runtimeConstraints: Array<{
        id: string;
        title: string;
        value: string;
        reason: string;
      }>;
      handoffStability: {
        status: "stable" | "watch" | "blocked";
        lecReady: boolean;
        jarvisReady: boolean;
        missingInputs: string[];
        rationale: string[];
      };
    };
    artifactRefs: {
      foundryContractV2: string;
      assemblyPlan: string;
      completenessValidator: string;
      externalIngestionScorecard: string;
    };
  };
  assemblyPlan: {
    archetypeId: string;
    brickIds: string[];
    moduleIds: string[];
    serviceIds: string[];
    primaryFlowIds: string[];
    assemblyOrder: Array<{
      id: string;
      title: string;
      kind: "foundation" | "module" | "integration" | "qa" | "release";
      source: "delivery-plan" | "planning-bundle" | "decision-engine" | "contract-runtime";
      dependsOn: string[];
      scopeIds: string[];
      acceptanceCriteria: string[];
    }>;
    securityPackSelection: {
      status: "locked";
      packId: string;
      title: string;
      controlIds: string[];
      approvalIds: string[];
      rbacTemplateId: string;
      rbacScopeIds: string[];
      tenantIsolationMode: "tenant-column" | "workspace-isolated" | "single-tenant" | "customer-boundary" | "schema-per-tenant";
      secretHandlingMode: "standard" | "managed" | "customer-managed" | "strict";
      ssoProfile: "not-required" | "sso-ready" | "sso-required";
      rollbackMode: "artifact-pack" | "controlled-cutover" | "tenant-wave" | "break-glass-recovery";
      sensitiveBrickIds: string[];
      evidenceBundleId: string;
      evidenceBundleArtifacts: string[];
      auditArtifacts: string[];
      rollbackArtifacts: string[];
      rationale: string[];
    };
    qaPackSelection: {
      status: "locked";
      packId: string;
      title: string;
      primaryFlowIds: string[];
      seededDataNeeds: string[];
      evidenceArtifacts: string[];
      rationale: string[];
    };
    buildBlockers: Array<{
      id: string;
      title: string;
      severity: "blocking" | "watch";
      source: "data-readiness" | "decision-trace" | "custom-delta" | "integration-pack" | "security-pack" | "qa-pack";
      resolution: string;
    }>;
    deliveryMode: "local-runnable" | "cloud-ready";
    qualityGates: string[];
    evidenceArtifacts: string[];
  };
  artifactUrls: {
    planningBundle: string;
    foundryContract: string;
    foundryContractV3: string;
    dataReadiness: string;
    integrationPack: string;
    uxThesis: string;
    securityThesis: string;
    customDelta: string;
    completenessValidator: string;
    externalIngestionScorecard: string;
    handoffContract: string;
    assemblyPlan: string;
    solutionPlan: string;
    serviceMap: string;
    domainModel: string;
    workflowGraph: string;
    integrationContracts: string;
    securityModel: string;
    qaPlan: string;
    deliveryPlan: string;
  };
}

export interface FoundryV2BrickSelectionRecord {
  status: "locked";
  rationale: string[];
  requiredBrickIds: string[];
  recommendedBrickIds: string[];
  optionalBrickIds: string[];
  rejectedBrickIds: string[];
  entries: Array<{
    brickId: string;
    title: string;
    summary: string;
    family: string;
    selection: "required" | "recommended" | "optional" | "rejected";
    source:
      | "planning-bundle"
      | "archetype-library"
      | "catalog-compatibility"
      | "governance-runtime"
      | "legacy-hydration";
    rationale: string[];
    sensitive: boolean;
  }>;
}

export interface FoundryDecisionChecklistSection {
  id: string;
  title: string;
  category: string;
  criticality: "low" | "medium" | "high" | "critical";
  status: "not_started" | "ai_proposed" | "needs_input" | "in_discussion" | "locked";
  blocking: boolean;
  recommendedOptionId: string | null;
  selectedOptionId: string | null;
  dependsOn: string[];
  rationalePreview: string[];
  note: string | null;
  promotedAt: string | null;
  promotedBy: "ai" | "human" | "hybrid" | null;
}

export interface FoundryDecisionRecommendationOption {
  id: string;
  label: string;
  description: string;
  stack: string[];
  suitableWhen: string[];
  caution: string[];
}

export interface FoundryDecisionRecommendationCard {
  sectionId: string;
  title: string;
  confidence: number;
  recommendedOptionId: string;
  rationale: string[];
  tradeoffs: string[];
  assumptions: string[];
  options: FoundryDecisionRecommendationOption[];
}

export interface FoundryDecisionDiscussionMessage {
  id: string;
  author: "user" | "assistant" | "system";
  createdAt: string;
  content: string;
}

export interface FoundryDecisionDiscussionThread {
  sectionId: string;
  status: "idle" | "active" | "resolved";
  messages: FoundryDecisionDiscussionMessage[];
}

export interface FoundryDecisionResolvedDecision {
  sectionId: string;
  optionId: string;
  label: string;
  summary: string;
  stack: string[];
  rationale: string[];
  impacts: string[];
  promotedAt: string;
  promotedBy: "ai" | "human" | "hybrid";
}

export interface FoundryDecisionEngineRecord {
  projectId: string;
  updatedAt: string;
  compositionDraftId: string | null;
  checklist: {
    sections: FoundryDecisionChecklistSection[];
  };
  recommendations: {
    cards: FoundryDecisionRecommendationCard[];
  };
  discussions: {
    threads: FoundryDecisionDiscussionThread[];
  };
  resolvedDecisions: {
    decisions: FoundryDecisionResolvedDecision[];
  };
  domainProfile: {
    domainFamily: string;
    operatingModel: string;
    archetypeId: string;
    title: string;
    summary: string;
    rationale: string[];
    requiredBrickIds: string[];
    governanceBrickIds: string[];
    recommendedCustomSliceIds: string[];
    mandatoryControls: string[];
    stackOptions: Array<{
      id: string;
      label: string;
      summary: string;
      stack: string[];
      suitableWhen: string[];
      caution: string[];
      why: string[];
      recommended: boolean;
    }>;
    keyQuestions: string[];
    proofScenarios: string[];
  };
  platformProfile: {
    developmentMode: "local_only" | "local_first" | "cloud_dev";
    productionHostingMode: "cloud_managed" | "customer_cloud" | "on_prem" | "hybrid";
    deploymentTopology: string;
    localityStrategy: string;
    complianceProfile: string;
    residencyStrategy: string;
    preferredStack: string;
    dataPlatform: string;
    authStrategy: string;
    qaStrategy: string;
    releaseStrategy: string;
    observabilityStrategy: string;
    securityTier: "standard" | "restricted" | "highly_regulated";
    summary: string[];
  };
  buildSummary: {
    status: "ready" | "watch" | "blocked";
    criticalLockedCount: number;
    criticalTotalCount: number;
    blockerSectionIds: string[];
    blockerLabels: string[];
    nextMoves: string[];
  };
  artifactUrls: {
    checklist: string;
    recommendations: string;
    discussions: string;
    resolvedDecisions: string;
    domainProfile: string;
    platformProfile: string;
  };
}

export interface FoundrySpecValidatorRecord {
  projectId: string;
  updatedAt: string;
  compositionDraftId: string | null;
  report: {
    id: string;
    schema: "longwalls.spec-validator@1";
    version: string;
    projectId: string;
    compositionDraftId: string | null;
    generatedAt: string;
    summary: {
      status: "ready" | "watch" | "blocked";
      score: number;
      readinessLabel: string;
      blockerCount: number;
      watchCount: number;
      promotedDecisionCount: number;
      criticalDecisionCount: number;
      headline: string;
    };
    checks: {
      specCompleteness: {
        status: "pass" | "watch" | "fail";
        score: number;
        summary: string;
        gaps: string[];
        contradictions: string[];
        requiredBeforeBuildReady: string[];
      };
      decisionCoverage: {
        status: "pass" | "watch" | "fail";
        score: number;
        summary: string;
        openCriticalSectionIds: string[];
        unresolvedSectionIds: string[];
        promotedSectionIds: string[];
      };
      securityPosture: {
        status: "pass" | "watch" | "fail";
        score: number;
        summary: string;
        securityTier: string;
        complianceProfile: string;
        blockerIds: string[];
        requiredSignalsMissing: string[];
      };
      deliveryReadiness: {
        status: "pass" | "watch" | "fail";
        score: number;
        summary: string;
        qaStrategy: string;
        releaseStrategy: string;
        handoffStatus: string;
        missingInputs: string[];
      };
      runtimeReadiness: {
        status: "pass" | "watch" | "fail";
        score: number;
        summary: string;
        buildReadyStatus: string;
        topology: string;
        hostingMode: string;
        runtimeConstraints: Array<{
          id: string;
          title: string;
          value: string;
          reason: string;
        }>;
      };
    };
    findings: Array<{
      id: string;
      title: string;
      severity: "blocking" | "watch" | "info";
      source:
        | "spec-completeness"
        | "decision-checklist"
        | "security-posture"
        | "delivery-readiness"
        | "runtime-constraints";
      summary: string;
      evidenceRefs: string[];
      nextAction: string | null;
    }>;
    nextMoves: string[];
    evidenceRefs: string[];
  };
  artifactUrls: {
    specValidator: string;
  };
}

export interface FoundryDecisionLedgerEntry {
  decisionId: string;
  sectionId: string;
  sectionTitle: string;
  category: string;
  criticality: "critical" | "high" | "medium";
  status: "needs_input" | "ai_proposed" | "in_discussion" | "locked";
  decisionSource: "ai" | "human" | "hybrid";
  decision: string;
  selectedOptionId: string | null;
  selectedLabel: string | null;
  recommendedOptionId: string | null;
  recommendedLabel: string | null;
  rationale: string[];
  tradeoffs: string[];
  assumptions: string[];
  impacts: string[];
  dependsOn: string[];
  locked: boolean;
  promotedAt: string | null;
  evidenceRefs: string[];
  notes: string[];
}

export interface FoundryDecisionLedgerRecord {
  projectId: string;
  updatedAt: string;
  compositionDraftId: string | null;
  decisionLedger: {
    id: string;
    schema: "longwalls.decision-ledger@1";
    version: string;
    projectId: string;
    compositionDraftId: string | null;
    generatedAt: string;
    sourceFoundryContractId: string | null;
    sourceContractV1DraftId: string | null;
    readiness: {
      status: "ready" | "watch" | "blocked";
      criticalLockedCount: number;
      criticalTotalCount: number;
      blockerSectionIds: string[];
      blockerLabels: string[];
      canCompileLecV2: boolean;
      nextMoves: string[];
    };
    entries: FoundryDecisionLedgerEntry[];
  };
  artifactUrls: {
    decisionLedger: string;
  };
}

export interface FoundryOrganizationMemorySourceProject {
  projectId: string;
  appName: string;
  isCurrentProject: boolean;
  domainFamily: string | null;
  preferredStack: string | null;
  readinessStatus: "ready" | "watch" | "blocked";
  patternIds: string[];
  decisionCount: number;
  failureLearningCount: number;
  headline: string;
  evidenceRefs: string[];
}

export interface FoundryOrganizationMemoryDecisionMemory {
  id: string;
  projectId: string;
  appName: string;
  domainFamily: string | null;
  preferredStack: string | null;
  sectionId: string;
  sectionTitle: string;
  category: string;
  criticality: "low" | "medium" | "high" | "critical";
  status: string;
  selectedLabel: string | null;
  recommendedLabel: string | null;
  locked: boolean;
  patternIds: string[];
  rationale: string[];
  evidenceRefs: string[];
}

export interface FoundryOrganizationMemoryPatternMemory {
  patternId: string;
  title: string;
  summary: string;
  tags: string[];
  observedProjectIds: string[];
  evidenceRefs: string[];
}

export interface FoundryOrganizationMemoryFailureLearning {
  id: string;
  projectId: string;
  appName: string;
  domainFamily: string | null;
  preferredStack: string | null;
  source: "decision-ledger" | "qa-factory" | "runtime-release";
  severity: "blocking" | "watch";
  title: string;
  summary: string;
  recommendation: string | null;
  patternIds: string[];
  evidenceRefs: string[];
}

export interface FoundryOrganizationMemoryRecommendation {
  id: string;
  title: string;
  recommendation: string;
  rationale: string[];
  confidence: number;
  domainFamily: string | null;
  preferredStack: string | null;
  patternIds: string[];
  sourceProjectIds: string[];
  evidenceRefs: string[];
}

export interface FoundryV2OrganizationMemoryRecord {
  projectId: string;
  updatedAt: string;
  organizationMemory: {
    id: string;
    schema: "longwalls.organization-memory@1";
    version: string;
    projectId: string;
    generatedAt: string;
    summary: {
      status: "ready" | "watch" | "blocked";
      canRecommend: boolean;
      sourceProjectCount: number;
      decisionMemoryCount: number;
      patternCount: number;
      failureLearningCount: number;
      recommendationCount: number;
      headline: string;
    };
    currentProject: {
      appName: string;
      domainFamily: string | null;
      preferredStack: string | null;
    };
    query: {
      availableDomainFamilies: string[];
      availablePreferredStacks: string[];
      availablePatternIds: string[];
      activeFilters: {
        domainFamily: string | null;
        preferredStack: string | null;
        patternId: string | null;
      };
      resultCounts: {
        sourceProjectCount: number;
        decisionMemoryCount: number;
        patternCount: number;
        failureLearningCount: number;
        recommendationCount: number;
      };
    };
    sourceProjects: FoundryOrganizationMemorySourceProject[];
    decisionMemories: FoundryOrganizationMemoryDecisionMemory[];
    patternMemories: FoundryOrganizationMemoryPatternMemory[];
    failureLearnings: FoundryOrganizationMemoryFailureLearning[];
    recommendations: FoundryOrganizationMemoryRecommendation[];
    blockers: string[];
    nextActions: string[];
    readableViews: {
      founder: string[];
      tactical: string[];
      operator: string[];
    };
  };
  artifactUrls: {
    organizationMemory: string;
  };
}

export interface FoundryV2LecV2Record {
  projectId: string;
  updatedAt: string;
  lec: {
    id: string;
    schema: "longwalls.lec@2";
    version: string;
    projectId: string;
    generatedAt: string;
    sourceRefs: {
      compositionDraftId: string | null;
      sourceFoundryContractId: string | null;
      sourceContractV1DraftId: string | null;
      decisionLedgerId: string;
    };
    app: {
      name: string;
      type: string;
      businessGoal: string;
      archetypeId: string;
      domainFamily: string;
      operatingModel: string;
    };
    decisionLedger: {
      status: "ready" | "watch" | "blocked";
      criticalLockedCount: number;
      criticalTotalCount: number;
      blockerSectionIds: string[];
      blockerLabels: string[];
    };
    architecture: {
      runtime: string;
      frontend: string;
      backend: string;
      database: string;
      deployment: string;
      auth: string;
      observability: string;
      preferredStack: string;
      deploymentTopology: string;
      residencyStrategy: string;
      complianceProfile: string;
    };
    dataModel: Array<{
      entity: string;
      purpose: string;
      sourceScope: string;
    }>;
    serviceSurface: {
      endpoints: Array<{
        method: "GET" | "POST" | "PUT" | "PATCH";
        path: string;
        description: string;
      }>;
      integrationTargets: string[];
      envKeys: string[];
      requiredBrickIds: string[];
      governanceBrickIds: string[];
      mandatoryControls: string[];
      customIntegrations: string[];
    };
    deliveryModel: {
      developmentMode: string;
      productionHostingMode: string;
      qaStrategy: string;
      releaseStrategy: string;
      observabilityStrategy: string;
      qualityGates: string[];
      evidenceChecklist: string[];
    };
    assemblyIntent: {
      deliveryMode: "local-runnable" | "cloud-ready";
      archetypeId: string;
      brickIds: string[];
      moduleIds: string[];
      serviceIds: string[];
      primaryFlowIds: string[];
      customDeltaSummary: string;
    };
    executionControl: {
      canLaunchJarvis: boolean;
      blockers: string[];
      nextMoves: string[];
      proofScenarios: string[];
      keyQuestions: string[];
    };
  };
  artifactUrls: {
    lecV2: string;
  };
}

export interface FoundryV2DeterministicAssemblyRecord {
  projectId: string;
  updatedAt: string;
  launchReadiness: "ready" | "blocked";
  compiler: {
    version: string;
    deterministic: true;
    compiledOutsideLlm: string[];
  };
  hashes: {
    lecV2: string;
    executionPlan: string;
    runSpec: string;
    assembly: string;
  };
  executionPlan: FoundryV2ExecutionPlanPreview;
  runSpecSummary: {
    schemaVersion: string;
    schemaId: string;
    workItemId: string;
    title: string;
    priority: "low" | "medium" | "high";
    tags: string[];
    flags: string[];
    launchReady: boolean;
    allowlistPaths: string[];
    denylistPaths: string[];
    stageCount: number;
    commandCounts: {
      prepare: number;
      test: number;
      build: number;
      e2e: number;
      audit: number;
    };
    mustPassChecks: Array<{
      id: string;
      description: string;
      commandRef: "prepare" | "test" | "build" | "e2e" | "audit";
    }>;
  };
  differentiation: {
    profileId: string | null;
    title: string | null;
    designVariantId: string | null;
    sourceRepoIds: string[];
    premiumBrickIds: string[];
    distinguishingAttributes: string[];
    selectedBrickCount: number;
    uniqueFamilyCount: number;
  };
  blockers: string[];
  artifactUrls: {
    deterministicAssembly: string;
    jarvisRunSpec: string;
    jarvisRunSpecValidationReport: string;
    lecV2: string;
  };
}

export interface FoundryV2TraceabilityNode {
  id: string;
  type:
    | "atlas-signal"
    | "spec-validator"
    | "decision-ledger"
    | "lec-v2"
    | "brick-selection"
    | "run-spec"
    | "qa-factory"
    | "jarvis-run"
    | "runtime-release"
    | "release-bundle"
    | "release-attestation"
    | "feedback";
  title: string;
  status: "ready" | "watch" | "missing" | "blocked";
  ref: string | null;
  artifactUrl: string | null;
  parentIds: string[];
  childIds?: string[];
  note: string | null;
}

export interface FoundryV2TraceabilityBlocker {
  id: string;
  severity: "blocking" | "watch";
  nodeId: string | null;
  edgeId?: string | null;
  reason: string;
  effect: "block_promotion" | "watch_only";
}

export interface FoundryV2DeliveryGraphReport {
  id: string;
  schema: "longwalls.delivery-graph@1";
  version: string;
  projectId: string;
  generatedAt: string;
  summary: {
    status: "ready" | "watch" | "blocked";
    canPromote: boolean;
    readyNodeCount: number;
    totalNodeCount: number;
    blockedEdgeCount: number;
    watchEdgeCount: number;
    orphanNodeCount: number;
    coverageScore: number;
    headline: string;
  };
  nodes: FoundryV2TraceabilityNode[];
  edges: Array<{
    id: string;
    from: string;
    to: string;
    label: string;
    status: "ready" | "watch" | "blocked";
    proofRefs: string[];
  }>;
  blockers: FoundryV2TraceabilityBlocker[];
  readableViews: {
    founder: string[];
    tactical: string[];
    operator: string[];
  };
}

export interface FoundryV2TraceabilityRecord {
  projectId: string;
  updatedAt: string;
  deliveryGraph: FoundryV2DeliveryGraphReport;
  traceability: {
    id: string;
    schema: "longwalls.traceability-contract@2";
    version: string;
    projectId: string;
    generatedAt: string;
    summary: {
      status: "ready" | "watch" | "blocked";
      readyNodeCount: number;
      totalNodeCount: number;
      blockingBlockerCount: number;
      watchBlockerCount: number;
      canPromote: boolean;
      headline: string;
    };
    nodes: FoundryV2TraceabilityNode[];
    edges: Array<{
      from: string;
      to: string;
      label: string;
    }>;
    blockers: FoundryV2TraceabilityBlocker[];
    readableViews: {
      founder: string[];
      tactical: string[];
      operator: string[];
    };
  };
  graphSample: {
    projectId: string;
    generatedAt: string;
    nodes: Array<
      Pick<FoundryV2TraceabilityNode, "id" | "type" | "status" | "parentIds" | "childIds">
    >;
    edges: Array<{
      id: string;
      from: string;
      to: string;
      label: string;
      status: "ready" | "watch" | "blocked";
      proofRefs: string[];
    }>;
  };
  blockersSample: {
    projectId: string;
    generatedAt: string;
    blockers: FoundryV2TraceabilityBlocker[];
  };
  artifactUrls: {
    deliveryGraph: string;
    traceabilityContract: string;
    traceabilityGraph: string;
    traceabilityBlockers: string;
  };
}

export interface FoundryV2RuntimeReleasePolicyPack {
  id: string;
  label: string;
  category: "architecture" | "security" | "rbac" | "observability" | "compliance" | "performance";
  verdict: "pass" | "watch" | "fail";
  summary: string;
  controls: string[];
  requiredArtifacts: string[];
  evidenceRefs: string[];
}

export interface FoundryV2RuntimeReleaseBlocker {
  id: string;
  severity: "blocking" | "watch";
  packId: string;
  reason: string;
}

export interface FoundryV2RuntimeProfileControl {
  id: "iam" | "secrets" | "audit" | "observability" | "backup" | "retention" | "rollback";
  label: string;
  status: "pass" | "watch" | "fail";
  summary: string;
}

export interface FoundryV2RuntimeProfileRecord {
  id: "local" | "local_by_design" | "customer_server" | "cloud_managed" | "regulated_hybrid";
  label: string;
  selected: boolean;
  recommended: boolean;
  verdict: "pass" | "watch" | "fail";
  summary: string;
  stackHint: string;
  operatingModel: string;
  goodFitFor: string[];
  controls: FoundryV2RuntimeProfileControl[];
  implications: string[];
  blockerReasons: string[];
}

export interface FoundryV2RuntimeStackRecommendation {
  selectedProfileId: FoundryV2RuntimeProfileRecord["id"];
  recommendedProfileId: FoundryV2RuntimeProfileRecord["id"];
  rationale: string;
  comparedOptions: Array<{
    profileId: FoundryV2RuntimeProfileRecord["id"];
    label: string;
    verdict: "pass" | "watch" | "fail";
    summary: string;
    implicationHeadline: string;
  }>;
}

export interface FoundryV2RuntimeReleaseRecord {
  schema: "longwalls.runtime-release@2";
  generatedAt: string;
  projectId: string;
  summary: {
    status: "ready" | "watch" | "blocked";
    canPromote: boolean;
    policyPackCount: number;
    blockingPackCount: number;
    watchPackCount: number;
    headline: string;
    rationale: string;
  };
  policyPacks: FoundryV2RuntimeReleasePolicyPack[];
  runtimeProfiles: FoundryV2RuntimeProfileRecord[];
  stackRecommendation: FoundryV2RuntimeStackRecommendation;
  blockers: FoundryV2RuntimeReleaseBlocker[];
  readableViews: {
    founder: string[];
    tactical: string[];
    operator: string[];
  };
  contracts: {
    runtime: Record<string, unknown>;
    deployment: Record<string, unknown>;
    rollback: {
      strategy: string;
      triggerSignals: string[];
      validationSteps: string[];
      operatorActions: string[];
      notes: string[];
    };
    support: {
      title: string;
      sourcePath: string;
      ownerLane: string;
      responseWindow: string;
      escalationWindow: string;
      sections: string[];
    };
    releaseAttestation: Record<string, unknown>;
  };
  artifactUrls: {
    policyPacks: string;
    runtimeContract: string;
    deploymentContract: string;
    rollbackPack: string;
    supportRunbook: string;
    releaseAttestation: string;
  };
}

export interface FoundryV2SimulationScenario {
  id: string;
  title: string;
  category: "spec" | "delivery" | "runtime" | "security" | "memory" | "cost" | "debt";
  severity: "blocking" | "watch" | "contained";
  reason: string;
  impact: string;
  nextAction: string;
  evidenceRefs: string[];
}

export interface FoundryV2SimulationPressure {
  id: string;
  label: string;
  status: "blocking" | "watch" | "contained";
  rationale: string;
  source?: string;
  mitigation?: string;
  evidenceRefs: string[];
}

export interface FoundryV2SimulationHotspot {
  id: string;
  label: string;
  severity: "blocking" | "watch" | "contained";
  ownerLane: string;
  reason: string;
  evidenceRefs: string[];
}

export interface FoundryV2SimulationEngineRecord {
  projectId: string;
  updatedAt: string;
  report: {
    kind: "simulation-report";
    schema: "longwalls.simulation-engine@1";
    id: string;
    version: string;
    projectId: string;
    generatedAt: string;
    summary: {
      status: "ready" | "watch" | "blocked";
      canPromote: boolean;
      launchConfidence: number;
      blockingScenarioCount: number;
      watchScenarioCount: number;
      dependencyCount: number;
      debtHotspotCount: number;
      costDriverCount: number;
      headline: string;
    };
    inputs: {
      specValidatorId: string | null;
      decisionLedgerId: string | null;
      lecId: string | null;
      deterministicAssemblyWorkItemId: string | null;
      qaFactoryId: string | null;
      deliveryGraphId: string | null;
      traceabilityId: string | null;
      runtimeReleaseId: string | null;
      organizationMemoryId: string | null;
      artifacts: Array<{
        id: string;
        label: string;
        status: "ready" | "watch" | "blocked" | "missing";
        artifactUrl: string | null;
        notes: string[];
      }>;
    };
    scenarios: FoundryV2SimulationScenario[];
    dependencyPressures: FoundryV2SimulationPressure[];
    debtHotspots: FoundryV2SimulationHotspot[];
    costDrivers: FoundryV2SimulationPressure[];
    nextActions: string[];
    readableViews: {
      founder: string[];
      tactical: string[];
      operator: string[];
    };
  };
  preflight: {
    verdict: "launch" | "hold";
    launchConfidence: number;
    blockerScenarioIds: string[];
    watchScenarioIds: string[];
    nextActions: string[];
    summary: string;
    shouldLaunch: boolean;
  };
  artifactUrls: {
    simulationReport: string;
  };
}

export interface FoundryV2JarvisEvaluationDimension {
  id:
    | "spec_compliance"
    | "code_quality"
    | "test_coverage"
    | "security"
    | "debt"
    | "repair_loop"
    | "governance";
  label: string;
  score: number;
  status: "ready" | "watch" | "blocked";
  rationale: string;
  evidenceRefs: string[];
}

export interface FoundryV2JarvisEvaluationRecord {
  projectId: string;
  updatedAt: string;
  latestRunId: string | null;
  report: {
    kind: "jarvis-evaluation-report";
    schema: "longwalls.jarvis-evaluation@1";
    id: string;
    version: string;
    projectId: string;
    runId: string;
    workItemId: string;
    generatedAt: string;
    inputs: {
      runSpecHash: string | null;
      configHash: string | null;
      policyDecision: string | null;
      globalStopReason: string | null;
      stopReasonDetail: string | null;
    };
    summary: {
      status: "ready" | "watch" | "blocked";
      verdict: "promote" | "repair" | "hold";
      score: number;
      canPromote: boolean;
      blockerCount: number;
      watchCount: number;
      headline: string;
      reasons: string[];
    };
    dimensions: FoundryV2JarvisEvaluationDimension[];
    evidence: {
      artifactCoverage: {
        runSummary: boolean;
        policyReport: boolean;
        metrics: boolean;
        executionCapsule: boolean;
        toolCatalog: boolean;
        judgeReport: boolean;
        repairLoopReport: boolean;
        evidenceContract: boolean;
        evidencePackManifest: boolean;
      };
      commandPosture: {
        plannedCommandCount: number | null;
        executedCommandCount: number | null;
        failedCommandCount: number | null;
        e2ePlannedCommandCount: number | null;
        e2eExecutedCommandCount: number | null;
        e2eFailedCommandCount: number | null;
        e2eSuccessRate: number | null;
        completenessRate: number | null;
        errorRate: number | null;
      };
      policyVerdict: string | null;
      stopReason: string | null;
      stopReasonDetail: string | null;
      guardrailViolationCount: number;
      failedStepGroup: string | null;
      failedStepCommand: string | null;
      failureSignatures: string[];
      requiredApproval: boolean;
    };
    repairLoop: {
      verdict: "converged" | "needs_approval" | "failed" | "no_op";
      convergenceScore: number;
      iterationsUsed: number;
      maxIterations: number;
      patchesProposed: number;
      patchesApplied: number;
      guardrailViolationCount: number;
      approvalRequired: boolean;
      failureSignatures: string[];
    };
    modelRuntime: {
      provider: string | null;
      model: string | null;
      baseUrl: string | null;
      reasoningEffort: string | null;
      deterministic: boolean;
      replaySafe: boolean;
    };
    nextActions: string[];
    readableViews: {
      founder: string[];
      tactical: string[];
      operator: string[];
    };
    artifactUrls: {
      runSummary: string | null;
      policyReport: string | null;
      metrics: string | null;
      executionCapsule: string | null;
      toolCatalog: string | null;
      judgeReport: string | null;
      repairLoopReport: string | null;
      evidenceContract: string | null;
      evidencePackManifest: string | null;
    };
  };
  preflight: {
    verdict: "launch" | "hold";
    score: number;
    blockerDimensionIds: string[];
    watchDimensionIds: string[];
    nextActions: string[];
    summary: string;
    shouldLaunch: boolean;
  };
  artifactUrls: {
    evaluationReport: string;
  };
}

export interface FoundryV2EclosiaBenchmarkAxis {
  id: "functionality" | "ux" | "debt" | "coverage" | "security";
  label: string;
  status: "ready" | "watch" | "blocked";
  manualScore: number;
  engineScore: number;
  gapToManual: number;
  formula: string;
  rationale: string;
  evidenceRefs: string[];
}

export interface FoundryV2EclosiaBenchmarkRecord {
  projectId: string;
  updatedAt: string;
  report: {
    kind: "eclosia-supremacy-benchmark";
    schema: "longwalls.eclosia-supremacy-benchmark@1";
    id: string;
    version: string;
    projectId: string;
    generatedAt: string;
    benchmark: {
      repoPath: string;
      comparisonSurface: string;
      parityMode: "bounded_dispatch_core";
      baselineApplication: "Conciergerie 2.0";
    };
    summary: {
      status: "ready" | "watch" | "blocked";
      verdict: "go_firm" | "go_conditional" | "no_go";
      engineScore: number;
      threshold: number;
      gapToManual: number;
      readyAxisCount: number;
      watchAxisCount: number;
      blockedAxisCount: number;
      headline: string;
    };
    comparison: {
      manualExisting: number;
      engineBlind: number;
      engineReuseBaseline: number;
      engineCurrent: number;
      threshold: number;
      gapToManual: number;
      absorbedLotCount: number;
      totalLotCount: number;
      rationale: string[];
    };
    benchmarkInputs: {
      deliveryGraph: {
        status: "ready" | "watch" | "blocked";
        canPromote: boolean;
        coverageScore: number;
        blockerCount: number;
        watchCount: number;
      };
      qaFactory: {
        status: "ready" | "watch" | "blocked";
        canPromote: boolean;
        coverageScore: number;
        acceptanceCriteriaCount: number;
        playwrightTemplateCount: number;
        blockerCount: number;
      };
      jarvisEvaluation: {
        status: "ready" | "watch" | "blocked";
        verdict: "promote" | "repair" | "hold";
        score: number;
        canPromote: boolean;
        blockerCount: number;
        watchCount: number;
      };
    };
    axes: FoundryV2EclosiaBenchmarkAxis[];
    blockers: string[];
    conditions: string[];
    nextActions: string[];
    artifactUrls: {
      blindScorecard: string | null;
      comparisonScorecard: string | null;
      deliveryGraph: string | null;
      qaFactory: string | null;
      jarvisEvaluation: string | null;
    };
  };
  artifactUrls: {
    benchmarkReport: string;
    benchmarkSummary: string;
  };
}

export interface FoundryV2RuntimeConnectorScope {
  id: string;
  label: string;
  status: "ready" | "watch" | "blocked";
  summary: string;
}

export interface FoundryV2RuntimeConnectorHealthCheck {
  id: string;
  label: string;
  status: "ready" | "watch" | "blocked";
  summary: string;
}

export interface FoundryV2RuntimeConnectorMapping {
  id: string;
  label: string;
  targetSurface: string;
  contractRefs: string[];
}

export interface FoundryV2RuntimeConnectorAssessment {
  status: "ready" | "watch" | "blocked";
  readyForExecution: boolean;
  mutationSafe: boolean;
  requiredEnvKeys: string[];
  declaredEnvKeys: string[];
  presentEnvKeys: string[];
  missingEnvKeys: string[];
  scopes: FoundryV2RuntimeConnectorScope[];
  healthChecks: FoundryV2RuntimeConnectorHealthCheck[];
  nextActions: string[];
}

export interface FoundryV2RuntimeConnectorRecord {
  id: string;
  label: string;
  family:
    | "source-control"
    | "ci-cd"
    | "identity"
    | "observability"
    | "runtime"
    | "data"
    | "payments"
    | "messaging"
    | "storage"
    | "integration";
  deliveryTool: string;
  summary: string;
  contractMappings: FoundryV2RuntimeConnectorMapping[];
  dryRun: FoundryV2RuntimeConnectorAssessment;
  live: FoundryV2RuntimeConnectorAssessment;
  notes: string[];
}

export interface FoundryV2RuntimeConnectorsRecord {
  projectId: string;
  updatedAt: string;
  report: {
    kind: "runtime-connectors-report";
    schema: "longwalls.runtime-connectors@1";
    id: string;
    version: string;
    projectId: string;
    generatedAt: string;
    summary: {
      status: "ready" | "watch" | "blocked";
      dryRunStatus: "ready" | "watch" | "blocked";
      liveStatus: "ready" | "watch" | "blocked";
      connectorCount: number;
      dryRunReadyCount: number;
      dryRunBlockedCount: number;
      liveReadyCount: number;
      liveWatchCount: number;
      liveBlockedCount: number;
      canExecuteDryRun: boolean;
      canMutateLive: boolean;
      headline: string;
    };
    environment: {
      appName: string;
      githubMode: string;
      repositoryName: string | null;
      complianceProfile: string | null;
      integrationTargets: string[];
      declaredEnvKeys: string[];
      availableEnvKeys: string[];
      missingEnvKeys: string[];
      notes: string[];
    };
    connectors: FoundryV2RuntimeConnectorRecord[];
    globalNextActions: string[];
  };
  artifactUrls: {
    runtimeConnectorsReport: string;
  };
}

export interface FoundryV2LaunchabilityCheckRecord {
  id: string;
  label: string;
  status: "pass" | "watch" | "fail";
  detail: string;
  evidenceRefs: string[];
}

export interface FoundryV2LaunchabilityBlockerRecord {
  id: string;
  severity: "blocking" | "watch";
  source: "handoff" | "spec-validator" | "build-ready" | "runtime-connectors" | "environment" | "runtime-plane";
  detail: string;
  resolution: string;
}

export interface FoundryV2LaunchabilityRecord {
  projectId: string;
  updatedAt: string;
  contract: {
    kind: "launchability-contract";
    schema: "longwalls.launchability@1";
    id: string;
    version: string;
    projectId: string;
    generatedAt: string;
    summary: {
      status: "launchable_from_foundry_green" | "watch" | "blocked";
      canLaunch: boolean;
      foundryGreen: boolean;
      blockerCount: number;
      watchCount: number;
      headline: string;
    };
    foundry: {
      handoffId: string | null;
      handoffStatus: string | null;
      buildReadyStatus: string | null;
      specValidatorStatus: string | null;
    };
    runtime: {
      executionMode: "direct" | "queued";
      connectorStatus: "ready" | "watch" | "blocked";
      dryRunStatus: "ready" | "watch" | "blocked";
      liveStatus: "ready" | "watch" | "blocked";
      requiredEnvKeys: string[];
      providedEnvKeys: string[];
      missingEnvKeys: string[];
    };
    checks: FoundryV2LaunchabilityCheckRecord[];
    blockers: FoundryV2LaunchabilityBlockerRecord[];
    nextActions: string[];
    evidenceRefs: string[];
  };
  artifactUrls: {
    launchabilityContract: string;
  };
}

export interface FoundryV2DomainPackKpi {
  id: string;
  label: string;
  description: string;
  target: string;
}

export interface FoundryV2DomainPackPolicyMapping {
  policyPackId: string;
  label: string;
  coverage: string;
  controlIds: string[];
  evidenceRefs: string[];
}

export interface FoundryV2DomainPackFlowDifference {
  id: string;
  label: string;
  summary: string;
  lane: string;
  proofPoints: string[];
}

export interface FoundryV2DomainPackGeneratedVariation {
  archetypeId: string;
  differentiationProfileId: string | null;
  designVariantId: string | null;
  recommendedBrickIds: string[];
  premiumBrickIds: string[];
  visibleAttributes: string[];
  uiSignature: string;
}

export interface FoundryV2DomainPackRecord {
  id: "finance" | "industry" | "health" | "support" | "field_ops";
  label: string;
  domainFamily: string;
  operatingModel: string;
  status: "ready" | "watch" | "blocked";
  isActive: boolean;
  summary: string;
  archetypeId: string;
  requiredBrickIds: string[];
  policyPackIds: string[];
  kpis: FoundryV2DomainPackKpi[];
  blockers: string[];
  policyMappings: FoundryV2DomainPackPolicyMapping[];
  flowDifferences: FoundryV2DomainPackFlowDifference[];
  generatedAppVariation: FoundryV2DomainPackGeneratedVariation;
  evidenceRefs: string[];
  notes: string[];
}

export interface FoundryV2DomainPacksRecord {
  projectId: string;
  updatedAt: string;
  report: {
    kind: "domain-packs-report";
    schema: "longwalls.domain-packs@1";
    id: string;
    version: string;
    projectId: string;
    generatedAt: string;
    summary: {
      status: "ready" | "watch" | "blocked";
      activePackId: FoundryV2DomainPackRecord["id"] | null;
      packCount: number;
      readyPackCount: number;
      watchPackCount: number;
      blockedPackCount: number;
      readyPackIds: FoundryV2DomainPackRecord["id"][];
      watchPackIds: FoundryV2DomainPackRecord["id"][];
      blockedPackIds: FoundryV2DomainPackRecord["id"][];
      variationScore: number;
      headline: string;
    };
    packs: FoundryV2DomainPackRecord[];
    globalNextActions: string[];
  };
  artifactUrls: {
    domainPacksReport: string;
  };
}

export interface FoundryV2QaFactoryAcceptanceCriterion {
  id: string;
  title: string;
  description: string;
  source: "decision-ledger" | "lec-v2" | "deterministic-assembly";
  mustPass: boolean;
  evidenceRefs: string[];
}

export interface FoundryV2QaFactoryEdgeCase {
  id: string;
  title: string;
  scenario: string;
  risk: "medium" | "high" | "critical";
  source: "decision-ledger" | "lec-v2" | "delivery-graph";
}

export interface FoundryV2QaFactoryPlaywrightTemplate {
  id: string;
  title: string;
  objective: string;
  steps: string[];
  assertions: string[];
  evidenceRefs: string[];
}

export interface FoundryV2QaFactoryRecord {
  projectId: string;
  updatedAt: string;
  report: {
    kind: "qa-factory-report";
    schema: "longwalls.qa-factory@1";
    id: string;
    version: string;
    projectId: string;
    generatedAt: string;
    summary: {
      status: "ready" | "watch" | "blocked";
      canPromote: boolean;
      coverageScore: number;
      acceptanceCriteriaCount: number;
      edgeCaseCount: number;
      testPlanCount: number;
      playwrightTemplateCount: number;
      headline: string;
    };
    inputs: {
      decisionLedgerId: string;
      lecId: string;
      qaStrategy: string;
      qualityGates: string[];
      evidenceChecklist: string[];
      launchReady: boolean;
    };
    acceptanceCriteria: FoundryV2QaFactoryAcceptanceCriterion[];
    edgeCases: FoundryV2QaFactoryEdgeCase[];
    testPlan: {
      prepare: string[];
      api: string[];
      ui: string[];
      release: string[];
    };
    playwrightTemplates: FoundryV2QaFactoryPlaywrightTemplate[];
    blockers: string[];
    nextActions: string[];
    readableViews: {
      founder: string[];
      tactical: string[];
      operator: string[];
    };
  };
  playwrightTemplate: {
    projectId: string;
    generatedAt: string;
    schemaVersion: "longwalls.qa-factory.playwright-template@1";
    qaStrategy: string;
    templates: FoundryV2QaFactoryPlaywrightTemplate[];
  };
  artifactUrls: {
    qaFactoryReport: string;
    playwrightTemplate: string;
  };
}

export type FoundryV2TradeoffLevel = "low" | "medium" | "high";

export interface FoundryV2ArchitectureRecommendation {
  appType: "internal-dashboard" | "customer-portal" | "api-platform" | "data-pipeline";
  runtime: "node" | "python" | "java";
  frontend: "nextjs" | "react-vite" | "nuxt";
  backend: "nestjs" | "express" | "fastapi" | "spring";
  database: "postgres" | "supabase" | "mongodb";
  deployment: "docker" | "vercel" | "cloudflare" | "aws";
  auth: "auth0" | "clerk" | "cognito" | "sso";
  observability: "otel-sentry" | "otel-datadog" | "basic-logs";
  confidence: number;
  rationale: string;
  tradeoffs: {
    cost: FoundryV2TradeoffLevel;
    complexity: FoundryV2TradeoffLevel;
    lockIn: FoundryV2TradeoffLevel;
    compliance: FoundryV2TradeoffLevel;
  };
}

export interface FoundryV2ArchitectureRecommendationResponse {
  generatedAt: string;
  recommendation: FoundryV2ArchitectureRecommendation;
  presets: {
    runtime: Array<FoundryV2ArchitectureRecommendation["runtime"]>;
    database: Array<FoundryV2ArchitectureRecommendation["database"]>;
    deployment: Array<FoundryV2ArchitectureRecommendation["deployment"]>;
  };
}

export interface FoundryV2ExecutionPlanStage {
  id: string;
  label: string;
  dependsOn: string[];
  commands: string[];
  acceptanceCriteria: string[];
  retryBudget: number;
  riskTier: "low" | "medium" | "high";
  visibility: "hidden" | "advanced";
}

export interface FoundryV2ExecutionPlanPreview {
  schemaId: string;
  schemaVersion: string;
  generatedAt: string;
  projectId: string;
  plannerMode: "engine-internal";
  appType: "internal-dashboard" | "customer-portal" | "api-platform" | "data-pipeline";
  budget: {
    maxIterations: number;
    maxMinutes: number;
  };
  stages: FoundryV2ExecutionPlanStage[];
}

export interface FoundryV2HandoffInput {
  appName: string;
  appType: string;
  businessGoal: string;
  constraints?: string;
  architecture: {
    runtime: string;
    frontend: string;
    backend: string;
    database: string;
    deployment: string;
    auth: string;
    observability: string;
  };
  environment: {
    runtimeProfile?: "local" | "local_by_design" | "customer_server" | "cloud_managed" | "regulated_hybrid";
    githubMode: string;
    repositoryName?: string;
    envKeys: string[];
    recommendedEnvKeys?: string[];
    integrationTargets?: string[];
    integrationNotes?: string;
    complianceProfile: string;
  };
  automation?: {
    autoPlanning?: boolean;
  };
}

export type FoundryV2HandoffArtifactKey =
  | "discoveryContext"
  | "discoveryReadiness"
  | "projectBrief"
  | "projectSpecs"
  | "specMvp"
  | "executionPlan"
  | "executionContract"
  | "handoffPackage"
  | "planningBundle"
  | "solutionPlan"
  | "serviceMap"
  | "domainModel"
  | "workflowGraph"
  | "integrationContracts"
  | "securityModel"
  | "qaPlan"
  | "deliveryPlan";

export interface FoundryV2HandoffRecord {
  id: string;
  projectId: string;
  createdAt: string;
  status: "READY_FOR_JARVIS" | "BLOCKED";
  input: FoundryV2HandoffInput;
  readiness: {
    decision: DiscoveryReadinessContract["decision"];
    status: DiscoveryReadinessContract["status"];
    score: number;
    openSignals: number;
    criticalSignals: number;
  };
  projectBriefStatus: string;
  specSectionCount: number;
  planner: {
    mode: "engine-internal";
    stageCount: number;
    budget: {
      maxIterations: number;
      maxMinutes: number;
    };
  };
  brickSelection?: FoundryV2BrickSelectionRecord | null;
  artifactUrls: Record<FoundryV2HandoffArtifactKey, string>;
}

export type FoundryV2ExecutionProvider = "stub" | "openai" | "rules_deterministic";
export type FoundryV2ExecutionEnvironment = "sandbox";
export type FoundryV2ExecutionStatus = "pending" | "running" | "succeeded" | "failed";
export type FoundryV2ExecutionDecision = "GO" | "NO_GO";
export type FoundryV2LaunchLanePhase = "queued" | "claimed" | "building" | "deploying" | "deployed" | "failed";

export interface FoundryV2LaunchLaneRecord {
  phase: FoundryV2LaunchLanePhase;
  updatedAt: string;
  currentStageId?: string | null;
  currentStageLabel?: string | null;
  history: Array<{
    phase: FoundryV2LaunchLanePhase;
    at: string;
    detail: string;
  }>;
}

export interface FoundryV2ExecutionRunRecord {
  id: string;
  projectId: string;
  handoffId: string;
  appName: string;
  provider: FoundryV2ExecutionProvider;
  environment: FoundryV2ExecutionEnvironment;
  dryRun: boolean;
  status: FoundryV2ExecutionStatus;
  decision: FoundryV2ExecutionDecision | null;
  startedAt: string;
  finishedAt: string | null;
  error: string | null;
  requiredEnvKeys: string[];
  missingEnvKeys: string[];
  envValuesProvided: string[];
  executionGraph: {
    stageCount: number;
    failedStageIds: string[];
    highRiskStageIds: string[];
    stageResults: Array<{
      id: string;
      label: string;
      riskTier: "low" | "medium" | "high";
      status: "passed" | "failed" | "skipped";
      attempts: number;
      retryBudget: number;
      reason?: string;
    }>;
  };
  approval: {
    required: boolean;
    provided: boolean;
    approved: boolean;
    approver: string | null;
    evidenceRef: string | null;
    note: string | null;
    highRiskStageIds: string[];
  };
  launchLane: FoundryV2LaunchLaneRecord;
  brickSelection?: FoundryV2BrickSelectionRecord | null;
  assembly: {
    status: "assembled" | "blocked";
    archetypeId: string | null;
    domainFamily: string | null;
    deliveryMode: "local-runnable" | "cloud-ready" | "unknown";
    moduleIds: string[];
    serviceIds: string[];
    primaryFlowIds: string[];
    qualityGates: string[];
    evidenceArtifacts: string[];
    customDeltaSummary: string | null;
    buildBlockers: {
      blocking: number;
      watch: number;
    };
    securityPack: {
      packId: string;
      title: string;
      controlIds: string[];
      approvalIds: string[];
      rbacTemplateId: string;
      rbacScopeIds: string[];
      tenantIsolationMode: string;
      secretHandlingMode: string;
      ssoProfile: string;
      rollbackMode: string;
      sensitiveBrickIds: string[];
      evidenceBundleId: string;
      evidenceBundleArtifacts: string[];
      auditArtifacts: string[];
      rollbackArtifacts: string[];
    } | null;
    qaPack: {
      packId: string;
      title: string;
      primaryFlowIds: string[];
      seededDataNeeds: string[];
      evidenceArtifacts: string[];
    } | null;
    steps: Array<{
      id: string;
      title: string;
      kind: "foundation" | "module" | "integration" | "qa" | "release";
      source: "delivery-plan" | "planning-bundle" | "decision-engine" | "contract-runtime";
      dependsOn: string[];
      scopeIds: string[];
      acceptanceCriteria: string[];
      riskTier: "low" | "medium" | "high";
      status: "passed" | "failed" | "skipped" | "pending";
      attempts: number;
      retryBudget: number;
      reason?: string;
    }>;
    summary: string[];
  } | null;
  archetype: {
    archetypeId: string;
    archiveUrl: string;
    outputDir: string;
    smokeResults: ArchetypeSmokeResult[];
    notes: string[];
    createdAt: string;
    preview?: {
      provider: "vercel";
      status: "skipped" | "blocked" | "failed" | "deployed";
      previewUrl: string | null;
      inspectUrl: string | null;
      deploymentId: string | null;
      readyState: string | null;
      stdoutExcerpt: string[];
      stderrExcerpt: string[];
      inspectSummary: Record<string, unknown> | null;
      inspectPath: string | null;
      rollbackCommand: string[];
      error: string | null;
      notes: string[];
      generatedAt: string;
    } | null;
  } | null;
  artifactUrls: {
    runSummary: string;
    executionSnapshot: string;
    deployHandoff: string;
    deliveryResult: string;
    vercelPreview: string;
  };
}

export interface FoundryV2ExecutionApprovalInput {
  highRiskStagesApproved: boolean;
  approver?: string;
  evidenceRef?: string;
  note?: string;
}

export type FoundryV2GovernanceApprovalDecision = "approved" | "rejected";
export type FoundryV2GovernanceApprovalStatus = "pending" | "approved" | "rejected";
export type FoundryV2GovernanceRiskTier = "low" | "medium" | "high" | "critical";
export type FoundryV2GovernanceEscalation = "none" | "watch" | "breached" | "escalated";

export interface FoundryV2GovernanceApprovalRecord {
  id: string;
  projectId: string;
  handoffId: string;
  runId: string;
  decision: FoundryV2GovernanceApprovalDecision | null;
  status: FoundryV2GovernanceApprovalStatus;
  approver: string | null;
  evidenceRef: string | null;
  note: string | null;
  highRiskStageIds: string[];
  requestedAt: string;
  decidedAt: string | null;
  riskTier: FoundryV2GovernanceRiskTier;
  policyHours: number;
  deadlineAt: string;
  escalation: FoundryV2GovernanceEscalation;
  remainingMs: number;
  overdueMs: number;
}

export interface FoundryV2GovernanceApprovalInput {
  runId: string;
  decision?: FoundryV2GovernanceApprovalDecision;
  approved?: boolean;
  approver: string;
  evidenceRef?: string;
  note?: string;
}

export interface FoundryV2ExecutionRunInput {
  provider: FoundryV2ExecutionProvider;
  environment: FoundryV2ExecutionEnvironment;
  dryRun: boolean;
  envValues: Record<string, string>;
  approval?: FoundryV2ExecutionApprovalInput;
}

export interface FoundryV2DeployCandidateRecord {
  run: FoundryV2ExecutionRunRecord;
  promotedByGovernance?: boolean;
  governanceApproval?: FoundryV2GovernanceApprovalRecord | null;
}

export type FoundryV2ControlTowerDecision = "GO" | "WATCH" | "BLOCKED";
export type FoundryV2ControlTowerMetricStatus = "good" | "watch" | "critical" | "unavailable";
export type FoundryV2ControlTowerStepStatus = "complete" | "in_progress" | "blocked" | "not_started";

export interface FoundryV2ControlTowerMetric {
  id: string;
  label: string;
  value: number | null;
  unit: "percent" | "count" | "minutes";
  status: FoundryV2ControlTowerMetricStatus;
  displayValue: string;
  detail: string;
}

export interface FoundryV2ControlTowerStep {
  id: string;
  label: string;
  status: FoundryV2ControlTowerStepStatus;
  detail: string;
}

export interface FoundryV2ControlTowerSnapshot {
  generatedAt: string;
  projectId: string;
  counts: {
    handoffs: number;
    runs: number;
    completedRuns: number;
    succeededRuns: number;
    goRuns: number;
    pendingApprovals: number;
    highRiskOpen: number;
  };
  latestHandoff: FoundryV2HandoffRecord | null;
  latestRun: FoundryV2ExecutionRunRecord | null;
  deployCandidate: FoundryV2DeployCandidateRecord | null;
  summary: {
    decision: FoundryV2ControlTowerDecision;
    score: number;
    headline: string;
    reasons: string[];
    nextActions: string[];
  };
  steps: FoundryV2ControlTowerStep[];
  metrics: FoundryV2ControlTowerMetric[];
}

export type FoundryV2ArtifactAssuranceStatus = "ready" | "watch" | "blocked";
export type FoundryV2ArtifactAssuranceCheckStatus = "ready" | "missing";

export interface FoundryV2ArtifactAssuranceCheck {
  id: string;
  label: string;
  fileName: string;
  url: string | null;
  status: FoundryV2ArtifactAssuranceCheckStatus;
  detail: string;
}

export interface FoundryV2ArtifactAssuranceSection {
  id: string | null;
  label: string;
  subject: string | null;
  status: FoundryV2ArtifactAssuranceStatus;
  availableCount: number;
  missingCount: number;
  checks: FoundryV2ArtifactAssuranceCheck[];
  nextActions: string[];
}

export interface FoundryV2ArtifactAssuranceRecord {
  schema: "longwalls.artifact-assurance@1";
  generatedAt: string;
  projectId: string;
  summary: {
    status: FoundryV2ArtifactAssuranceStatus;
    headline: string;
    reasons: string[];
    nextActions: string[];
    availableCount: number;
    missingCount: number;
  };
  latestHandoff: FoundryV2ArtifactAssuranceSection | null;
  latestRun: FoundryV2ArtifactAssuranceSection | null;
  deployCandidate: FoundryV2ArtifactAssuranceSection | null;
}

export interface FoundryV2ExecutivePackRecord {
  schema: "longwalls.executive-pack@1";
  generatedAt: string;
  projectId: string;
  summary: {
    decision: FoundryV2ControlTowerDecision;
    score: number;
    headline: string;
    boardNarrative: string;
    operatingModel: string;
  };
  businessContext: {
    appName: string | null;
    appType: string | null;
    businessGoal: string | null;
    mvpScope: string[];
    kpis: string[];
    hypotheses: string[];
    openQuestions: string[];
  };
  architecture: {
    runtime: string | null;
    frontend: string | null;
    backend: string | null;
    database: string | null;
    deployment: string | null;
    auth: string | null;
    observability: string | null;
    rationale: string | null;
    confidence: number | null;
  };
  delivery: {
    latestHandoffId: string | null;
    latestRunId: string | null;
    latestRunStatus: string | null;
    deployCandidateRunId: string | null;
    promotedByGovernance: boolean;
    pendingApprovals: number;
    highRiskOpen: number;
    keyMetrics: Array<{
      id: string;
      label: string;
      displayValue: string;
      status: FoundryV2ControlTowerMetricStatus;
      detail: string;
    }>;
  };
  rollout: {
    currentMode: FoundryV2CutoverPhase;
    promotionSignal: "PROMOTE_PILOT" | "PROMOTE_STAGED" | "PROMOTE_DEFAULT" | "HOLD";
    nextPromotionPhase: FoundryV2CutoverPhase | null;
    stableReviewWindows: number;
    requiredStableReviewWindows: number;
    eligibleForDefaultPromotion: boolean;
    latestReviewWindowLabel: string | null;
    latestWavePhase: FoundryV2RolloutWavePhase | null;
    latestWaveStatus: FoundryV2RolloutWaveStatus | null;
    latestOutcome: FoundryV2RolloutWaveOutcome | null;
    promotionNarrative: string;
  };
  risks: Array<{
    id: string;
    title: string;
    level: "watch" | "critical";
    mitigation: string;
  }>;
  nextActions: string[];
  artifacts: {
    executivePackJson: string;
    executivePackMarkdown: string;
    contract: string | null;
    assessment: string | null;
    specDraft: string | null;
    handoffPackage: string | null;
    executionContract: string | null;
    deployHandoff: string | null;
    deliveryResult: string | null;
    runSummary: string | null;
    cutoverDashboardJson: string;
    cutoverDashboardMarkdown: string;
  };
}

export type FoundryV2ReadinessDecision = "GO" | "WATCH" | "NO_GO";
export type FoundryV2ReadinessChecklistStatus = "pass" | "watch" | "fail";

export interface FoundryV2ReadinessPackRecord {
  schema: "longwalls.readiness-pack@1";
  generatedAt: string;
  projectId: string;
  gate: {
    id: "JV2-H4";
    label: string;
    status: "READY_FOR_REVIEW";
  };
  summary: {
    decision: FoundryV2ReadinessDecision;
    score: number;
    headline: string;
    rationale: string;
  };
  checklist: Array<{
    id: string;
    label: string;
    status: FoundryV2ReadinessChecklistStatus;
    detail: string;
    evidenceLinks: string[];
  }>;
  approvals: {
    pending: number;
    latestDecision: FoundryV2GovernanceApprovalDecision | null;
    latestApprover: string | null;
    latestEvidenceRef: string | null;
    highRiskOpen: number;
    evidenceRefs: string[];
  };
  risks: Array<{
    id: string;
    title: string;
    level: "watch" | "critical";
    mitigation: string;
  }>;
  rollback: {
    available: boolean;
    strategy: string;
    notes: string[];
    triggerSignals: string[];
    validationSteps: string[];
  };
  evidence: {
    readinessPackJson: string;
    readinessPackMarkdown: string;
    controlTower: string;
    executivePackJson: string;
    executivePackMarkdown: string;
    contract: string | null;
    assessment: string | null;
    specDraft: string | null;
    handoffPackage: string | null;
    executionContract: string | null;
    runSummary: string | null;
    deployHandoff: string | null;
    deliveryResult: string | null;
    governanceEvidenceRefs: string[];
  };
  nextActions: string[];
}

export type FoundryV2CutoverGateStatus = "pass" | "watch" | "fail";
export type FoundryV2CutoverDecision = "PROMOTE_PILOT" | "PROMOTE_STAGED" | "PROMOTE_DEFAULT" | "HOLD" | "BLOCKED";
export type FoundryV2CutoverPhase = "parallel_beta" | "pilot" | "staged" | "default";
export type FoundryV2RolloutWavePhase = "pilot" | "staged" | "default";
export type FoundryV2RolloutWaveStatus = "planned" | "monitoring" | "completed" | "rolled_back";
export type FoundryV2RolloutWaveOutcome = "stable" | "watch" | "failed" | "rolled_back";
export type FoundryV2TenantRolloutMode = "disabled" | "pilot" | "staged" | "default" | "legacy-fallback-only";
export type FoundryV2RouteResolutionTarget = "foundry-v2" | "jarvis-v2" | "governance-v2";

export interface FoundryV2RolloutWaveRecord {
  id: string;
  projectId: string;
  phase: FoundryV2RolloutWavePhase;
  status: FoundryV2RolloutWaveStatus;
  outcome: FoundryV2RolloutWaveOutcome;
  reviewWindowLabel: string;
  tenantIds: string[];
  evidenceRefs: string[];
  note: string | null;
  observedAt: string;
  signals: {
    controlTowerDecision: FoundryV2ControlTowerDecision;
    controlTowerScore: number;
    readinessDecision: FoundryV2ReadinessDecision;
    readinessScore: number;
    executiveDecision: FoundryV2ControlTowerDecision;
    executiveScore: number;
    cutoverScore: number;
  };
}

export interface FoundryV2RolloutWaveInput {
  phase: FoundryV2RolloutWavePhase;
  status?: FoundryV2RolloutWaveStatus;
  outcome?: FoundryV2RolloutWaveOutcome;
  reviewWindowLabel?: string;
  tenantIds?: string[];
  evidenceRefs?: string[];
  note?: string;
  observedAt?: string;
}

export interface FoundryV2CutoverDashboardRecord {
  schema: "longwalls.cutover-dashboard@1";
  generatedAt: string;
  projectId: string;
  summary: {
    decision: FoundryV2CutoverDecision;
    recommendedPhase: FoundryV2CutoverPhase;
    score: number;
    confidence: number;
    headline: string;
    rationale: string;
  };
  counts: {
    pass: number;
    watch: number;
    fail: number;
  };
  gates: Array<{
    id: "security" | "quality" | "functionality" | "qa" | "governance" | "rollout";
    label: string;
    status: FoundryV2CutoverGateStatus;
    score: number;
    summary: string;
    blockers: string[];
    nextActions: string[];
    evidenceLinks: string[];
  }>;
  rolloutPlan: {
    currentMode: FoundryV2CutoverPhase;
    recommendedPhase: FoundryV2CutoverPhase;
    rationale: string;
    guardrails: string[];
    exitCriteria: string[];
    rollbackSwitch: string;
  };
  rolloutHistory: {
    stableReviewWindows: number;
    requiredStableReviewWindows: number;
    eligibleForDefaultPromotion: boolean;
    latestWavePhase: FoundryV2RolloutWavePhase | null;
    latestWaveStatus: FoundryV2RolloutWaveStatus | null;
    latestOutcome: FoundryV2RolloutWaveOutcome | null;
    waves: Array<{
      id: string;
      phase: FoundryV2RolloutWavePhase;
      status: FoundryV2RolloutWaveStatus;
      outcome: FoundryV2RolloutWaveOutcome;
      reviewWindowLabel: string;
      observedAt: string;
      tenantCount: number;
      evidenceRefs: string[];
      note: string | null;
      signals: {
        controlTowerDecision: FoundryV2ControlTowerDecision;
        controlTowerScore: number;
        readinessDecision: FoundryV2ReadinessDecision;
        readinessScore: number;
        executiveDecision: FoundryV2ControlTowerDecision;
        executiveScore: number;
        cutoverScore: number;
      };
    }>;
  };
  scorecard: {
    controlTowerDecision: FoundryV2ControlTowerDecision;
    controlTowerScore: number;
    readinessDecision: FoundryV2ReadinessDecision;
    readinessScore: number;
    executiveDecision: FoundryV2ControlTowerDecision;
    executiveScore: number;
    pendingApprovals: number;
    highRiskOpen: number;
    latestRunStatus: string | null;
    deployCandidateRunId: string | null;
    stableReviewWindows: number;
    currentRolloutPhase: FoundryV2CutoverPhase;
  };
  artifacts: {
    cutoverDashboardJson: string;
    cutoverDashboardMarkdown: string;
    controlTower: string;
    readinessPackJson: string;
    readinessPackMarkdown: string;
    executivePackJson: string;
    executivePackMarkdown: string;
    runSummary: string | null;
    deployHandoff: string | null;
    deliveryResult: string | null;
  };
  nextActions: string[];
}

export interface FoundryV2TenantRolloutStateRecord {
  schema: "longwalls.rollout-state@1";
  id: string;
  projectId: string;
  tenantId: string;
  mode: FoundryV2TenantRolloutMode;
  activePath: "legacy" | "jarvis-v2";
  legacyFallbackEnabled: boolean;
  source: "manual" | "derived";
  decidedBy: string | null;
  note: string | null;
  evidenceRefs: string[];
  sourceWaveId: string | null;
  updatedAt: string;
}

export interface FoundryV2TenantRolloutStateInput {
  mode: FoundryV2TenantRolloutMode;
  legacyFallbackEnabled?: boolean;
  decidedBy?: string;
  note?: string;
  evidenceRefs?: string[];
  sourceWaveId?: string;
}

export type FoundryV2TenantRolloutAction =
  | "promote-pilot"
  | "promote-staged"
  | "promote-default"
  | "switch-legacy-fallback"
  | "disable-v2";

export interface FoundryV2TenantRolloutActionDescriptor {
  action: FoundryV2TenantRolloutAction;
  label: string;
  nextMode: FoundryV2TenantRolloutMode;
  intent: "promote" | "fallback" | "disable";
  reason: string;
  recommended: boolean;
  requiresEvidence: boolean;
}

export interface FoundryV2TenantRolloutTransitionRecord {
  schema: "longwalls.rollout-transition@1";
  id: string;
  projectId: string;
  tenantId: string;
  action: FoundryV2TenantRolloutAction;
  previousMode: FoundryV2TenantRolloutMode;
  nextMode: FoundryV2TenantRolloutMode;
  previousActivePath: "legacy" | "jarvis-v2";
  nextActivePath: "legacy" | "jarvis-v2";
  releaseStatus: "ready" | "watch" | "blocked";
  cutoverDecision: FoundryV2CutoverDecision;
  recommendedActionAtDecision: FoundryV2TenantRolloutAction | null;
  decidedBy: string;
  note: string | null;
  evidenceRefs: string[];
  sourceWaveId: string | null;
  occurredAt: string;
}

export interface FoundryV2TenantRolloutControlRecord {
  schema: "longwalls.rollout-control@1";
  generatedAt: string;
  projectId: string;
  tenantId: string;
  currentState: FoundryV2TenantRolloutStateRecord;
  summary: {
    releaseStatus: "ready" | "watch" | "blocked";
    cutoverDecision: FoundryV2CutoverDecision;
    headline: string;
    rationale: string;
  };
  recommendedAction: FoundryV2TenantRolloutActionDescriptor | null;
  allowedActions: FoundryV2TenantRolloutActionDescriptor[];
  operatorGuidance: string[];
  transitionHistory: FoundryV2TenantRolloutTransitionRecord[];
}

export interface FoundryV2TenantRolloutActionInput {
  action: FoundryV2TenantRolloutAction;
  decidedBy?: string;
  note?: string;
  evidenceRefs?: string[];
  sourceWaveId?: string;
}

export interface FoundryV2RouteResolutionRecord {
  schema: "longwalls.route-resolution@1";
  generatedAt: string;
  projectId: string;
  tenantId: string;
  target: FoundryV2RouteResolutionTarget;
  featureFlagEnabled: boolean;
  rolloutMode: FoundryV2TenantRolloutMode;
  activePath: "legacy" | "jarvis-v2";
  routeAccess: {
    targetEnabled: boolean;
    legacyFallbackEnabled: boolean;
    sidebarEntryVisible: boolean;
    v2ScreensEnabled: boolean;
  };
  recommendation: {
    status: "allow" | "redirect";
    route: string;
    reason: string;
  };
}

export interface FoundryV2NavigationSnapshotRecord {
  schema: "longwalls.navigation-snapshot@1";
  generatedAt: string;
  projectId: string;
  tenantId: string;
  featureFlagEnabled: boolean;
  rolloutMode: FoundryV2TenantRolloutMode;
  activePath: "legacy" | "jarvis-v2";
  legacyFallbackEnabled: boolean;
  targets: {
    foundryV2: FoundryV2RouteResolutionRecord;
    jarvisV2: FoundryV2RouteResolutionRecord;
    governanceV2: FoundryV2RouteResolutionRecord;
  };
  moduleEntry: {
    id: "jarvis_v2";
    visible: boolean;
    label: string;
    preferredHref: string;
    fallbackHref: string;
    reason: string;
  };
  shellBanner: {
    tone: "emerald" | "amber" | "orange" | "slate";
    headline: string;
    detail: string;
    ctaLabel: string;
    ctaHref: string;
  };
}

export interface FoundryV2ReleaseBundleRecord {
  schema: "longwalls.release-bundle@1";
  generatedAt: string;
  projectId: string;
  tenantId: string;
  summary: {
    releaseStatus: "ready" | "watch" | "blocked";
    decision: FoundryV2CutoverDecision;
    activePath: "legacy" | "jarvis-v2";
    rolloutMode: FoundryV2TenantRolloutMode;
    headline: string;
    rationale: string;
  };
  tenantRollout: FoundryV2TenantRolloutStateRecord;
  environment: {
    repositoryName: string | null;
    requiredEnvKeys: string[];
    integrationTargets: string[];
    complianceProfile: string | null;
  };
  gates: Array<{
    id: "readiness" | "cutover" | "tenant-rollout" | "traceability" | "runtime-release";
    label: string;
    status: "pass" | "watch" | "fail";
    score: number;
    summary: string;
    evidenceLinks: string[];
  }>;
  operations: {
    promoteChecklist: string[];
    rollbackChecklist: string[];
    guardrails: string[];
    releaseNotes: string[];
  };
  artifacts: {
    releaseBundleJson: string;
    releaseBundleMarkdown: string;
    controlTower: string;
    executivePackJson: string;
    executivePackMarkdown: string;
    readinessPackJson: string;
    readinessPackMarkdown: string;
    cutoverDashboardJson: string;
    cutoverDashboardMarkdown: string;
    runtimeContract: string;
    deploymentContract: string;
    rollbackPack: string;
    supportRunbook: string;
    releaseAttestationV2: string;
    deliveryGraph: string;
    traceabilityContract: string;
    traceabilityGraph: string;
    traceabilityBlockers: string;
    handoffPackage: string | null;
    executionContract: string | null;
    runSummary: string | null;
    deployHandoff: string | null;
    deliveryResult: string | null;
  };
  nextActions: string[];
}

export interface FoundryV2PortfolioReleaseBoardItem {
  projectId: string;
  projectCode: string;
  projectName: string;
  updatedAt: string;
  customerId: string | null;
  customerName: string | null;
  appName: string | null;
  latestHandoffId: string | null;
  latestRunId: string | null;
  v2Started: boolean;
  releaseStatus: "ready" | "watch" | "blocked" | "not_started";
  rolloutMode: FoundryV2TenantRolloutMode;
  activePath: "legacy" | "jarvis-v2";
  cutoverDecision: FoundryV2CutoverDecision | "HOLD";
  controlTowerDecision: FoundryV2ControlTowerDecision;
  confidenceScore: number;
  pendingApprovals: number;
  highRiskOpen: number;
  stableReviewWindows: number;
  requiredStableReviewWindows: number;
  headline: string;
  nextAction: string;
}

export interface FoundryV2PortfolioReleaseBoardRecord {
  schema: "longwalls.portfolio-release-board@1";
  generatedAt: string;
  tenantId: string;
  summary: {
    totalProjects: number;
    v2Started: number;
    ready: number;
    watch: number;
    blocked: number;
    notStarted: number;
    defaultMode: number;
    fallbackOnly: number;
    headline: string;
    focusAreas: string[];
  };
  items: FoundryV2PortfolioReleaseBoardItem[];
}

export interface FoundryV2GovernanceConsoleRecord {
  schema: "longwalls.governance-console@1";
  generatedAt: string;
  projectId: string;
  selectedRunId: string | null;
  portfolioBoard: FoundryV2PortfolioReleaseBoardRecord | null;
  latestHandoff: FoundryV2HandoffRecord | null;
  runs: FoundryV2ExecutionRunRecord[];
  latestApproval: FoundryV2GovernanceApprovalRecord | null;
  approvalHistory: FoundryV2GovernanceApprovalRecord[];
  readinessPack: FoundryV2ReadinessPackRecord | null;
  cutoverDashboard: FoundryV2CutoverDashboardRecord | null;
  rolloutState: FoundryV2TenantRolloutStateRecord | null;
  rolloutControl: FoundryV2TenantRolloutControlRecord | null;
  releaseBundle: FoundryV2ReleaseBundleRecord | null;
  errors: {
    portfolioBoard: string | null;
    runs: string | null;
    approvals: string | null;
    readinessPack: string | null;
    cutoverDashboard: string | null;
    rolloutState: string | null;
    rolloutControl: string | null;
    releaseBundle: string | null;
  };
}

export class FoundryV2HandoffError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly readiness?: DiscoveryReadinessContract;

  constructor(message: string, status: number, options?: { code?: string; readiness?: DiscoveryReadinessContract }) {
    super(message);
    this.name = "FoundryV2HandoffError";
    this.status = status;
    this.code = options?.code;
    this.readiness = options?.readiness;
  }
}

export class FoundryV2ContractV1Error extends Error {
  readonly status: number;
  readonly code?: string;
  readonly validationErrors?: FoundryContractV1ValidationError[];

  constructor(
    message: string,
    status: number,
    options?: { code?: string; validationErrors?: FoundryContractV1ValidationError[] },
  ) {
    super(message);
    this.name = "FoundryV2ContractV1Error";
    this.status = status;
    this.code = options?.code;
    this.validationErrors = options?.validationErrors;
  }
}

async function readJsonSafely<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

async function readErrorPayload(response: Response): Promise<FoundryV2ApiErrorPayload> {
  const payload = await readJsonSafely<FoundryV2ApiErrorPayload>(response);
  return payload ?? {};
}

export async function fetchFoundryV2ContextGate(projectId: string): Promise<FoundryV2ContextGateResponse> {
  if (!projectId?.trim()) {
    throw new Error("projectId is required");
  }

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/context`);
  const payload = (await readJsonSafely(response)) as
    | (FoundryV2ContextGateResponse & FoundryV2ApiErrorPayload)
    | FoundryV2ApiErrorPayload;

  if (!response.ok || payload?.ok === false || !(payload as FoundryV2ContextGateResponse).context) {
    const message = (payload as FoundryV2ApiErrorPayload)?.error ?? `Failed to load Foundry V2 context (status ${response.status})`;
    throw new Error(message);
  }

  return payload as FoundryV2ContextGateResponse;
}

export async function createFoundryV2ContractV1Draft(
  projectId: string,
  input: FoundryContractV1DraftInput,
): Promise<FoundryContractV1DraftRecord> {
  if (!projectId?.trim()) {
    throw new Error("projectId is required");
  }

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/contract-v1/ingest`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const payload = await readErrorPayload(response);
    const message = payload.error ?? `Failed to create Foundry Contract v1 draft (status ${response.status})`;
    throw new FoundryV2ContractV1Error(message, response.status, {
      code: payload.code,
      validationErrors: payload.validationErrors,
    });
  }

  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    draft?: FoundryContractV1DraftRecord;
    error?: string;
  };
  if (payload?.ok === false || !payload?.draft) {
    throw new Error(payload?.error ?? "Foundry Contract v1 draft response is invalid");
  }
  return payload.draft;
}

export async function fetchLatestFoundryV2ContractV1Draft(projectId: string): Promise<FoundryContractV1DraftRecord | null> {
  if (!projectId?.trim()) {
    throw new Error("projectId is required");
  }

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/contract-v1/latest`);
  if (response.status === 404) {
    return null;
  }
  const payload = (await readJsonSafely(response)) as { ok?: boolean; draft?: FoundryContractV1DraftRecord; error?: string };
  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load latest Foundry Contract v1 draft (status ${response.status})`);
  }
  return payload?.draft ?? null;
}

export async function fetchFoundryV2ContractV1Draft(
  projectId: string,
  draftId: string,
): Promise<FoundryContractV1DraftRecord | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");
  if (!draftId?.trim()) throw new Error("draftId is required");

  const response = await fetch(
    `/api/v2/projects/${encodeURIComponent(projectId)}/foundry/contract-v1/drafts/${encodeURIComponent(draftId)}`,
  );
  if (response.status === 404) return null;

  const payload = (await readJsonSafely(response)) as { ok?: boolean; draft?: FoundryContractV1DraftRecord; error?: string };
  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load Foundry Contract v1 draft (status ${response.status})`);
  }
  return payload?.draft ?? null;
}

export async function createFoundryV2CompositionDraft(
  projectId: string,
  input: Partial<FoundryV2HandoffInput>,
): Promise<FoundryV2CompositionDraftRecord> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/contract-v2/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });

  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; draft?: FoundryV2CompositionDraftRecord; error?: string }
    | null;

  if (!response.ok || payload?.ok === false || !payload?.draft) {
    throw new Error(payload?.error ?? `Failed to generate Foundry Contract v2 (status ${response.status})`);
  }
  return payload.draft;
}

export async function importLegacyRepoFoundryExternalIntake(
  projectId: string,
  repoId: string,
): Promise<FoundryLegacyExternalIntakeResponse> {
  if (!projectId?.trim()) throw new Error("projectId is required");
  if (!repoId?.trim()) throw new Error("repoId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/external-intake/legacy`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ repoId: repoId.trim() }),
  });

  const payload = (await readJsonSafely(response)) as
    | ({
        ok?: boolean;
        intake?: FoundryLegacyExternalIntakeRecord;
        contractDraft?: FoundryContractV1DraftRecord;
        compositionDraft?: FoundryV2CompositionDraftRecord;
        error?: string;
      } & FoundryV2ApiErrorPayload)
    | null;

  if (!response.ok || payload?.ok === false || !payload?.intake || !payload?.contractDraft || !payload?.compositionDraft) {
    throw new Error(payload?.error ?? `Failed to import legacy repo into Foundry (status ${response.status})`);
  }

  return {
    intake: payload.intake,
    contractDraft: payload.contractDraft,
    compositionDraft: payload.compositionDraft,
  };
}

export async function fetchLatestFoundryV2CompositionDraft(
  projectId: string,
): Promise<FoundryV2CompositionDraftRecord | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/contract-v2/latest`);
  if (response.status === 404) {
    return null;
  }

  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; draft?: FoundryV2CompositionDraftRecord; error?: string }
    | null;

  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load latest Foundry Contract v2 draft (status ${response.status})`);
  }
  return payload?.draft ?? null;
}

export async function fetchFoundryDecisionEngine(
  projectId: string,
): Promise<FoundryDecisionEngineRecord | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/decision-checklist`);
  if (response.status === 404) return null;

  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; decisionEngine?: FoundryDecisionEngineRecord; error?: string }
    | null;

  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load Foundry decision checklist (status ${response.status})`);
  }
  return payload?.decisionEngine ?? null;
}

export async function fetchFoundrySpecValidator(
  projectId: string,
): Promise<FoundrySpecValidatorRecord | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/spec-validator`);
  if (response.status === 404) return null;

  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; specValidator?: FoundrySpecValidatorRecord; error?: string }
    | null;

  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load Foundry spec validator (status ${response.status})`);
  }
  return payload?.specValidator ?? null;
}

export async function fetchFoundryDecisionLedger(
  projectId: string,
): Promise<FoundryDecisionLedgerRecord | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/decision-ledger`);
  if (response.status === 404) return null;

  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; decisionLedger?: FoundryDecisionLedgerRecord; error?: string }
    | null;

  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load Foundry decision ledger (status ${response.status})`);
  }
  return payload?.decisionLedger ?? null;
}

export async function fetchFoundryV2OrganizationMemory(
  projectId: string,
): Promise<FoundryV2OrganizationMemoryRecord | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/org-memory`);
  if (response.status === 404) return null;

  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; organizationMemory?: FoundryV2OrganizationMemoryRecord; error?: string }
    | null;

  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load Foundry organization memory (status ${response.status})`);
  }
  return payload?.organizationMemory ?? null;
}

export async function fetchFoundryV2LecV2(
  projectId: string,
): Promise<FoundryV2LecV2Record | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/lec-v2`);
  if (response.status === 404) return null;

  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; lec?: FoundryV2LecV2Record; error?: string }
    | null;

  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load Foundry LEC v2 (status ${response.status})`);
  }
  return payload?.lec ?? null;
}

export async function fetchFoundryV2DeterministicAssembly(
  projectId: string,
): Promise<FoundryV2DeterministicAssemblyRecord | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/deterministic-assembly`);
  if (response.status === 404) return null;

  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; deterministicAssembly?: FoundryV2DeterministicAssemblyRecord; error?: string }
    | null;

  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load deterministic assembly (status ${response.status})`);
  }
  return payload?.deterministicAssembly ?? null;
}

export async function fetchFoundryV2Traceability(
  projectId: string,
): Promise<FoundryV2TraceabilityRecord | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/traceability`);
  if (response.status === 404) return null;

  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; traceability?: FoundryV2TraceabilityRecord; error?: string }
    | null;

  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load Foundry traceability (status ${response.status})`);
  }
  return payload?.traceability ?? null;
}

export async function fetchFoundryV2QaFactory(
  projectId: string,
): Promise<FoundryV2QaFactoryRecord | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/qa-factory`);
  if (response.status === 404) return null;

  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; qaFactory?: FoundryV2QaFactoryRecord; error?: string }
    | null;

  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load Foundry QA factory (status ${response.status})`);
  }
  return payload?.qaFactory ?? null;
}

export async function fetchFoundryV2RuntimeRelease(
  projectId: string,
): Promise<FoundryV2RuntimeReleaseRecord | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/runtime-release`);
  if (response.status === 404) return null;

  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; runtimeRelease?: FoundryV2RuntimeReleaseRecord; error?: string }
    | null;

  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load Foundry runtime release (status ${response.status})`);
  }
  return payload?.runtimeRelease ?? null;
}

export async function fetchFoundryV2SimulationEngine(
  projectId: string,
): Promise<FoundryV2SimulationEngineRecord | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/simulation-engine`);
  if (response.status === 404) return null;

  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; simulationEngine?: FoundryV2SimulationEngineRecord; error?: string }
    | null;

  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load Foundry simulation engine (status ${response.status})`);
  }
  return payload?.simulationEngine ?? null;
}

export async function fetchFoundryV2RuntimeConnectors(
  projectId: string,
): Promise<FoundryV2RuntimeConnectorsRecord | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/runtime-connectors`);
  if (response.status === 404) return null;

  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; runtimeConnectors?: FoundryV2RuntimeConnectorsRecord; error?: string }
    | null;

  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load Foundry runtime connectors (status ${response.status})`);
  }
  return payload?.runtimeConnectors ?? null;
}

export async function fetchFoundryV2Launchability(
  projectId: string,
): Promise<FoundryV2LaunchabilityRecord | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/launchability`);
  if (response.status === 404) return null;

  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; launchability?: FoundryV2LaunchabilityRecord; error?: string }
    | null;

  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load Foundry launchability (status ${response.status})`);
  }
  return payload?.launchability ?? null;
}

export async function fetchFoundryV2DomainPacks(
  projectId: string,
): Promise<FoundryV2DomainPacksRecord | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/domain-packs`);
  if (response.status === 404) return null;

  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; domainPacks?: FoundryV2DomainPacksRecord; error?: string }
    | null;

  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load Foundry domain packs (status ${response.status})`);
  }
  return payload?.domainPacks ?? null;
}

export async function fetchFoundryV2EclosiaBenchmark(
  projectId: string,
): Promise<FoundryV2EclosiaBenchmarkRecord | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/eclosia-benchmark`);
  if (response.status === 404) return null;

  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; eclosiaBenchmark?: FoundryV2EclosiaBenchmarkRecord; error?: string }
    | null;

  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load the Eclosia benchmark (status ${response.status})`);
  }
  return payload?.eclosiaBenchmark ?? null;
}

export async function fetchFoundryV2JarvisEvaluation(
  projectId: string,
): Promise<FoundryV2JarvisEvaluationRecord | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/jarvis-evaluation`);
  if (response.status === 404) return null;

  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; jarvisEvaluation?: FoundryV2JarvisEvaluationRecord; error?: string }
    | null;

  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load Foundry Jarvis evaluation (status ${response.status})`);
  }
  return payload?.jarvisEvaluation ?? null;
}

export async function refreshFoundryDecisionEngine(
  projectId: string,
): Promise<FoundryDecisionEngineRecord> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/decision-checklist/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; decisionEngine?: FoundryDecisionEngineRecord; error?: string }
    | null;

  if (!response.ok || payload?.ok === false || !payload?.decisionEngine) {
    throw new Error(payload?.error ?? `Failed to refresh Foundry decision checklist (status ${response.status})`);
  }
  return payload.decisionEngine;
}

export async function createFoundryDecisionDiscussion(
  projectId: string,
  sectionId: string,
  input: { content: string; author?: "user" | "assistant" | "system" },
): Promise<FoundryDecisionEngineRecord> {
  if (!projectId?.trim()) throw new Error("projectId is required");
  if (!sectionId?.trim()) throw new Error("sectionId is required");
  if (!input.content?.trim()) throw new Error("content is required");

  const response = await fetch(
    `/api/v2/projects/${encodeURIComponent(projectId)}/foundry/decision-checklist/${encodeURIComponent(sectionId)}/discussions`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; decisionEngine?: FoundryDecisionEngineRecord; error?: string }
    | null;

  if (!response.ok || payload?.ok === false || !payload?.decisionEngine) {
    throw new Error(payload?.error ?? `Failed to append Foundry decision discussion (status ${response.status})`);
  }
  return payload.decisionEngine;
}

export async function promoteFoundryDecision(
  projectId: string,
  sectionId: string,
  input: { optionId: string; note?: string; promotedBy?: "human" | "hybrid" },
): Promise<FoundryDecisionEngineRecord> {
  if (!projectId?.trim()) throw new Error("projectId is required");
  if (!sectionId?.trim()) throw new Error("sectionId is required");
  if (!input.optionId?.trim()) throw new Error("optionId is required");

  const response = await fetch(
    `/api/v2/projects/${encodeURIComponent(projectId)}/foundry/decision-checklist/${encodeURIComponent(sectionId)}/promote`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  const payload = (await readJsonSafely(response)) as
    | { ok?: boolean; decisionEngine?: FoundryDecisionEngineRecord; error?: string }
    | null;

  if (!response.ok || payload?.ok === false || !payload?.decisionEngine) {
    throw new Error(payload?.error ?? `Failed to promote Foundry decision (status ${response.status})`);
  }
  return payload.decisionEngine;
}

export async function fetchFoundryV2ArchitectureRecommendation(
  projectId: string,
  appType: string,
): Promise<FoundryV2ArchitectureRecommendationResponse> {
  if (!projectId?.trim()) {
    throw new Error("projectId is required");
  }

  const params = new URLSearchParams();
  if (appType?.trim()) params.set("appType", appType.trim());
  const query = params.toString();
  const response = await fetch(
    `/api/v2/projects/${encodeURIComponent(projectId)}/foundry/architecture-recommendation${query ? `?${query}` : ""}`,
  );
  const payload = (await readJsonSafely(response)) as
    | (FoundryV2ArchitectureRecommendationResponse & FoundryV2ApiErrorPayload)
    | FoundryV2ApiErrorPayload;

  if (!response.ok || payload?.ok === false || !(payload as FoundryV2ArchitectureRecommendationResponse).recommendation) {
    const message =
      (payload as FoundryV2ApiErrorPayload)?.error ??
      `Failed to load architecture recommendation (status ${response.status})`;
    throw new Error(message);
  }

  return payload as FoundryV2ArchitectureRecommendationResponse;
}

export async function fetchFoundryV2ExecutionPlanPreview(
  projectId: string,
  appType: string,
): Promise<FoundryV2ExecutionPlanPreview> {
  if (!projectId?.trim()) {
    throw new Error("projectId is required");
  }

  const params = new URLSearchParams();
  if (appType?.trim()) params.set("appType", appType.trim());
  const query = params.toString();
  const response = await fetch(
    `/api/v2/projects/${encodeURIComponent(projectId)}/foundry/execution-plan-preview${query ? `?${query}` : ""}`,
  );
  const payload = (await readJsonSafely(response)) as
    | ({ ok?: boolean; executionPlan?: FoundryV2ExecutionPlanPreview; error?: string } & FoundryV2ApiErrorPayload)
    | FoundryV2ApiErrorPayload;

  if (!response.ok || payload?.ok === false || !(payload as any).executionPlan) {
    const message =
      (payload as FoundryV2ApiErrorPayload)?.error ??
      `Failed to load execution plan preview (status ${response.status})`;
    throw new Error(message);
  }

  return (payload as { executionPlan: FoundryV2ExecutionPlanPreview }).executionPlan;
}

export async function createFoundryV2Handoff(
  projectId: string,
  input: FoundryV2HandoffInput,
  options: { force?: boolean } = {},
): Promise<FoundryV2HandoffRecord> {
  if (!projectId?.trim()) {
    throw new Error("projectId is required");
  }

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/handoffs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ input, ...(options.force ? { force: true } : {}) }),
  });

  if (!response.ok) {
    const payload = await readErrorPayload(response);
    const message = payload.error ?? `Failed to create Foundry V2 handoff (status ${response.status})`;
    throw new FoundryV2HandoffError(message, response.status, {
      code: payload.code,
      readiness: payload.readiness,
    });
  }

  const payload = (await readJsonSafely(response)) as { ok?: boolean; handoff?: FoundryV2HandoffRecord; error?: string };

  if (payload?.ok === false || !payload?.handoff) {
    throw new Error(payload?.error ?? "Foundry V2 handoff response is invalid");
  }

  return payload.handoff;
}

export async function fetchLatestFoundryV2Handoff(projectId: string): Promise<FoundryV2HandoffRecord | null> {
  if (!projectId?.trim()) {
    throw new Error("projectId is required");
  }

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/handoffs/latest`);
  if (response.status === 404) {
    return null;
  }

  const payload = (await readJsonSafely(response)) as { ok?: boolean; handoff?: FoundryV2HandoffRecord; error?: string };
  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load latest Foundry V2 handoff (status ${response.status})`);
  }

  return payload?.handoff ?? null;
}

export async function fetchFoundryV2Handoff(
  projectId: string,
  handoffId: string,
): Promise<FoundryV2HandoffRecord | null> {
  if (!projectId?.trim()) {
    throw new Error("projectId is required");
  }
  if (!handoffId?.trim()) {
    throw new Error("handoffId is required");
  }

  const response = await fetch(
    `/api/v2/projects/${encodeURIComponent(projectId)}/foundry/handoffs/${encodeURIComponent(handoffId)}`,
  );
  if (response.status === 404) {
    return null;
  }

  const payload = (await readJsonSafely(response)) as { ok?: boolean; handoff?: FoundryV2HandoffRecord; error?: string };
  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load Foundry V2 handoff (status ${response.status})`);
  }

  return payload?.handoff ?? null;
}

export async function createFoundryV2ExecutionRun(
  projectId: string,
  handoffId: string,
  input: FoundryV2ExecutionRunInput,
): Promise<FoundryV2ExecutionRunRecord> {
  if (!projectId?.trim()) throw new Error("projectId is required");
  if (!handoffId?.trim()) throw new Error("handoffId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/handoffs/${encodeURIComponent(handoffId)}/runs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    run?: FoundryV2ExecutionRunRecord;
    error?: string;
    blockers?: Array<{ id?: string; detail?: string; resolution?: string }>;
    missingEnvKeys?: string[];
    requiredEnvKeys?: string[];
  };

  if (!response.ok || payload?.ok === false || !payload?.run) {
    const details =
      Array.isArray(payload?.missingEnvKeys) && payload.missingEnvKeys.length
        ? ` Missing keys: ${payload.missingEnvKeys.join(", ")}.`
        : "";
    const blockerDetails =
      Array.isArray(payload?.blockers) && payload.blockers.length
        ? ` Blockers: ${payload.blockers
            .map((blocker) => blocker.detail ?? blocker.id ?? "unknown blocker")
            .join(" | ")}.`
        : "";
    throw new Error(
      (payload?.error ?? `Failed to create Foundry V2 run (status ${response.status})`) + details + blockerDetails,
    );
  }
  return payload.run;
}

export async function listFoundryV2ExecutionRuns(
  projectId: string,
  handoffId: string,
): Promise<FoundryV2ExecutionRunRecord[]> {
  if (!projectId?.trim()) throw new Error("projectId is required");
  if (!handoffId?.trim()) throw new Error("handoffId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/handoffs/${encodeURIComponent(handoffId)}/runs`);
  const payload = (await readJsonSafely(response)) as { ok?: boolean; runs?: FoundryV2ExecutionRunRecord[]; error?: string };

  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to list Foundry V2 runs (status ${response.status})`);
  }
  return payload.runs ?? [];
}

export async function fetchFoundryV2ExecutionRun(
  projectId: string,
  handoffId: string,
  runId: string,
): Promise<FoundryV2ExecutionRunRecord | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");
  if (!handoffId?.trim()) throw new Error("handoffId is required");
  if (!runId?.trim()) throw new Error("runId is required");

  const response = await fetch(
    `/api/v2/projects/${encodeURIComponent(projectId)}/foundry/handoffs/${encodeURIComponent(handoffId)}/runs/${encodeURIComponent(runId)}`,
  );
  if (response.status === 404) return null;

  const payload = (await readJsonSafely(response)) as { ok?: boolean; run?: FoundryV2ExecutionRunRecord; error?: string };
  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load Foundry V2 run (status ${response.status})`);
  }
  return payload.run ?? null;
}

export async function fetchFoundryV2DeployCandidate(
  projectId: string,
  handoffId: string,
): Promise<FoundryV2DeployCandidateRecord | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");
  if (!handoffId?.trim()) throw new Error("handoffId is required");

  const response = await fetch(
    `/api/v2/projects/${encodeURIComponent(projectId)}/foundry/handoffs/${encodeURIComponent(handoffId)}/deploy-candidate`,
  );
  if (response.status === 404) return null;

  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    run?: FoundryV2ExecutionRunRecord;
    promotedByGovernance?: boolean;
    governanceApproval?: FoundryV2GovernanceApprovalRecord;
    error?: string;
  };
  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load deploy candidate (status ${response.status})`);
  }
  if (!payload.run) return null;
  return {
    run: payload.run,
    promotedByGovernance: payload.promotedByGovernance === true,
    governanceApproval: payload.governanceApproval ?? null,
  };
}

export async function fetchFoundryV2ControlTower(projectId: string): Promise<FoundryV2ControlTowerSnapshot> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/control-tower`);
  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    controlTower?: FoundryV2ControlTowerSnapshot;
    error?: string;
  };

  if (!response.ok || payload?.ok === false || !payload?.controlTower) {
    throw new Error(payload?.error ?? `Failed to load Foundry V2 control tower (status ${response.status})`);
  }

  return payload.controlTower;
}

export async function fetchFoundryV2ArtifactAssurance(projectId: string): Promise<FoundryV2ArtifactAssuranceRecord> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/artifact-assurance`);
  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    artifactAssurance?: FoundryV2ArtifactAssuranceRecord;
    error?: string;
  };

  if (!response.ok || payload?.ok === false || !payload?.artifactAssurance) {
    throw new Error(payload?.error ?? `Failed to load Foundry V2 artifact assurance (status ${response.status})`);
  }

  return payload.artifactAssurance;
}

export async function fetchFoundryV2ExecutivePack(projectId: string): Promise<FoundryV2ExecutivePackRecord> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/executive-pack`);
  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    executivePack?: FoundryV2ExecutivePackRecord;
    error?: string;
  };

  if (!response.ok || payload?.ok === false || !payload?.executivePack) {
    throw new Error(payload?.error ?? `Failed to load Foundry V2 executive pack (status ${response.status})`);
  }

  return payload.executivePack;
}

export async function fetchFoundryV2ReadinessPack(projectId: string): Promise<FoundryV2ReadinessPackRecord> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/readiness-pack`);
  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    readinessPack?: FoundryV2ReadinessPackRecord;
    error?: string;
  };

  if (!response.ok || payload?.ok === false || !payload?.readinessPack) {
    throw new Error(payload?.error ?? `Failed to load Foundry V2 readiness pack (status ${response.status})`);
  }

  return payload.readinessPack;
}

export async function fetchFoundryV2CutoverDashboard(projectId: string): Promise<FoundryV2CutoverDashboardRecord> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/cutover-dashboard`);
  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    cutoverDashboard?: FoundryV2CutoverDashboardRecord;
    error?: string;
  };

  if (!response.ok || payload?.ok === false || !payload?.cutoverDashboard) {
    throw new Error(payload?.error ?? `Failed to load Foundry V2 cutover dashboard (status ${response.status})`);
  }

  return payload.cutoverDashboard;
}

export async function fetchFoundryV2RolloutState(projectId: string): Promise<FoundryV2TenantRolloutStateRecord> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/rollout-state`);
  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    rolloutState?: FoundryV2TenantRolloutStateRecord;
    error?: string;
  };

  if (!response.ok || payload?.ok === false || !payload?.rolloutState) {
    throw new Error(payload?.error ?? `Failed to load Foundry V2 rollout state (status ${response.status})`);
  }

  return payload.rolloutState;
}

export async function fetchFoundryV2RouteResolution(
  projectId: string,
  target: FoundryV2RouteResolutionTarget,
): Promise<FoundryV2RouteResolutionRecord> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const params = new URLSearchParams({ target });
  const response = await fetch(
    `/api/v2/projects/${encodeURIComponent(projectId)}/foundry/route-resolution?${params.toString()}`,
  );
  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    routeResolution?: FoundryV2RouteResolutionRecord;
    error?: string;
  };

  if (!response.ok || payload?.ok === false || !payload?.routeResolution) {
    throw new Error(payload?.error ?? `Failed to resolve Foundry V2 route (status ${response.status})`);
  }

  return payload.routeResolution;
}

export async function fetchFoundryV2NavigationSnapshot(
  projectId: string,
): Promise<FoundryV2NavigationSnapshotRecord> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/navigation-snapshot`);
  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    navigationSnapshot?: FoundryV2NavigationSnapshotRecord;
    error?: string;
  };

  if (!response.ok || payload?.ok === false || !payload?.navigationSnapshot) {
    throw new Error(payload?.error ?? `Failed to resolve Foundry V2 navigation snapshot (status ${response.status})`);
  }

  return payload.navigationSnapshot;
}

export async function createFoundryV2RolloutState(
  projectId: string,
  input: FoundryV2TenantRolloutStateInput,
): Promise<FoundryV2TenantRolloutStateRecord> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/rollout-state`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    rolloutState?: FoundryV2TenantRolloutStateRecord;
    error?: string;
  };

  if (!response.ok || payload?.ok === false || !payload?.rolloutState) {
    throw new Error(payload?.error ?? `Failed to update Foundry V2 rollout state (status ${response.status})`);
  }

  return payload.rolloutState;
}

export async function fetchFoundryV2RolloutControl(projectId: string): Promise<FoundryV2TenantRolloutControlRecord> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/rollout-control`);
  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    rolloutControl?: FoundryV2TenantRolloutControlRecord;
    error?: string;
  };

  if (!response.ok || payload?.ok === false || !payload?.rolloutControl) {
    throw new Error(payload?.error ?? `Failed to load Foundry V2 rollout control (status ${response.status})`);
  }

  return payload.rolloutControl;
}

export async function applyFoundryV2RolloutAction(
  projectId: string,
  input: FoundryV2TenantRolloutActionInput,
): Promise<{
  rolloutState: FoundryV2TenantRolloutStateRecord;
  rolloutControl: FoundryV2TenantRolloutControlRecord;
  transition: FoundryV2TenantRolloutTransitionRecord;
}> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/rollout-control/actions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    rolloutState?: FoundryV2TenantRolloutStateRecord;
    rolloutControl?: FoundryV2TenantRolloutControlRecord;
    transition?: FoundryV2TenantRolloutTransitionRecord;
    error?: string;
  };

  if (!response.ok || payload?.ok === false || !payload?.rolloutState || !payload?.rolloutControl || !payload?.transition) {
    throw new Error(payload?.error ?? `Failed to apply Foundry V2 rollout action (status ${response.status})`);
  }

  return {
    rolloutState: payload.rolloutState,
    rolloutControl: payload.rolloutControl,
    transition: payload.transition,
  };
}

export async function fetchFoundryV2ReleaseBundle(projectId: string): Promise<FoundryV2ReleaseBundleRecord> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/release-bundle`);
  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    releaseBundle?: FoundryV2ReleaseBundleRecord;
    error?: string;
  };

  if (!response.ok || payload?.ok === false || !payload?.releaseBundle) {
    throw new Error(payload?.error ?? `Failed to load Foundry V2 release bundle (status ${response.status})`);
  }

  return payload.releaseBundle;
}

export async function fetchFoundryV2PortfolioReleaseBoard(): Promise<FoundryV2PortfolioReleaseBoardRecord> {
  const response = await fetch("/api/v2/foundry/portfolio-release-board");
  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    board?: FoundryV2PortfolioReleaseBoardRecord;
    error?: string;
  };

  if (!response.ok || payload?.ok === false || !payload?.board) {
    throw new Error(payload?.error ?? `Failed to load Foundry V2 portfolio release board (status ${response.status})`);
  }

  return payload.board;
}

export async function fetchFoundryV2GovernanceConsole(
  projectId: string,
  options: { runId?: string } = {},
): Promise<FoundryV2GovernanceConsoleRecord> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const params = new URLSearchParams();
  if (options.runId?.trim()) {
    params.set("runId", options.runId.trim());
  }
  const query = params.toString();
  const response = await fetch(
    `/api/v2/projects/${encodeURIComponent(projectId)}/foundry/governance-console${query ? `?${query}` : ""}`,
  );
  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    governanceConsole?: FoundryV2GovernanceConsoleRecord;
    error?: string;
  };

  if (!response.ok || payload?.ok === false || !payload?.governanceConsole) {
    throw new Error(payload?.error ?? `Failed to load Foundry V2 governance console (status ${response.status})`);
  }

  return payload.governanceConsole;
}

export async function listFoundryV2RolloutWaves(projectId: string): Promise<FoundryV2RolloutWaveRecord[]> {
  if (!projectId?.trim()) throw new Error("projectId is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/rollout-waves`);
  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    rolloutWaves?: FoundryV2RolloutWaveRecord[];
    error?: string;
  };

  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load Foundry V2 rollout waves (status ${response.status})`);
  }

  return payload.rolloutWaves ?? [];
}

export async function createFoundryV2RolloutWave(
  projectId: string,
  input: FoundryV2RolloutWaveInput,
): Promise<FoundryV2RolloutWaveRecord> {
  if (!projectId?.trim()) throw new Error("projectId is required");
  if (!input.phase) throw new Error("phase is required");

  const response = await fetch(`/api/v2/projects/${encodeURIComponent(projectId)}/foundry/rollout-waves`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    rolloutWave?: FoundryV2RolloutWaveRecord;
    error?: string;
  };

  if (!response.ok || payload?.ok === false || !payload?.rolloutWave) {
    throw new Error(payload?.error ?? `Failed to create Foundry V2 rollout wave (status ${response.status})`);
  }

  return payload.rolloutWave;
}

export async function fetchLatestFoundryV2GovernanceApproval(
  projectId: string,
  handoffId: string,
  options: { runId?: string } = {},
): Promise<FoundryV2GovernanceApprovalRecord | null> {
  if (!projectId?.trim()) throw new Error("projectId is required");
  if (!handoffId?.trim()) throw new Error("handoffId is required");

  const params = new URLSearchParams();
  if (options.runId?.trim()) params.set("runId", options.runId.trim());
  const query = params.toString();
  const response = await fetch(
    `/api/v2/projects/${encodeURIComponent(projectId)}/foundry/handoffs/${encodeURIComponent(handoffId)}/governance-approvals/latest${query ? `?${query}` : ""}`,
  );
  if (response.status === 404) return null;

  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    approval?: FoundryV2GovernanceApprovalRecord;
    error?: string;
  };
  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to load latest Governance V2 approval (status ${response.status})`);
  }
  return payload.approval ?? null;
}

export async function listFoundryV2GovernanceApprovals(
  projectId: string,
  handoffId: string,
  options: { runId?: string } = {},
): Promise<FoundryV2GovernanceApprovalRecord[]> {
  if (!projectId?.trim()) throw new Error("projectId is required");
  if (!handoffId?.trim()) throw new Error("handoffId is required");

  const params = new URLSearchParams();
  if (options.runId?.trim()) params.set("runId", options.runId.trim());
  const query = params.toString();
  const response = await fetch(
    `/api/v2/projects/${encodeURIComponent(projectId)}/foundry/handoffs/${encodeURIComponent(handoffId)}/governance-approvals${query ? `?${query}` : ""}`,
  );
  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    approvals?: FoundryV2GovernanceApprovalRecord[];
    error?: string;
  };
  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error ?? `Failed to list Governance V2 approvals (status ${response.status})`);
  }
  return payload.approvals ?? [];
}

export async function createFoundryV2GovernanceApproval(
  projectId: string,
  handoffId: string,
  input: FoundryV2GovernanceApprovalInput,
): Promise<FoundryV2GovernanceApprovalRecord> {
  if (!projectId?.trim()) throw new Error("projectId is required");
  if (!handoffId?.trim()) throw new Error("handoffId is required");
  if (!input.runId?.trim()) throw new Error("runId is required");
  if (!input.approver?.trim()) throw new Error("approver is required");

  const response = await fetch(
    `/api/v2/projects/${encodeURIComponent(projectId)}/foundry/handoffs/${encodeURIComponent(handoffId)}/governance-approvals`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...input,
        runId: input.runId.trim(),
        approver: input.approver.trim(),
        evidenceRef: input.evidenceRef?.trim() || undefined,
        note: input.note?.trim() || undefined,
      }),
    },
  );
  const payload = (await readJsonSafely(response)) as {
    ok?: boolean;
    approval?: FoundryV2GovernanceApprovalRecord;
    error?: string;
  };

  if (!response.ok || payload?.ok === false || !payload.approval) {
    throw new Error(payload?.error ?? `Failed to create Governance V2 approval (status ${response.status})`);
  }
  return payload.approval;
}
